import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { getDefaultUILanguage } from './utils/localeUtils'

const resources = {
  en: {
    translation: {
      // App title
      appTitle: 'Voxist AI Dev Portal',
      tagline: 'Speech AI Platform - ASR & TTS',
      
      // Login form
      welcomeBack: 'Welcome back',
      signInPrompt: 'Sign in to your developer account',
      signInWithEmail: 'Sign in with Email',
      signInWithGoogle: 'Sign in with Google',
      signInWithMicrosoft: 'Sign in with Microsoft',
      
      // User menu
      userMenu: 'User menu',
      billing: 'Billing',
      usage: 'Usage',
      apiKeys: 'API Keys',
      signOut: 'Sign Out',
      
      // Language selector
      selectLanguage: 'Select Language',
      
      // Common
      close: 'Close',
      cancel: 'Cancel',
      save: 'Save',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      
      // API Keys
      apiKeyManagement: 'API Key Management',
      createApiKey: 'Create API Key',
      apiKeyName: 'API Key Name',
      apiKeyDescription: 'Description',
      generateKey: 'Generate Key',
      copyKey: 'Copy Key',
      keyCreated: 'API Key Created',
      keyRotated: 'API Key Rotated',
      rotateKey: 'Rotate Key',
      deleteKey: 'Delete Key',
      confirmDelete: 'Are you sure you want to delete this API key?',
      keyDeleted: 'API Key Deleted',
      showKey: 'Show Key',
      hideKey: 'Hide Key',
      lastUsed: 'Last Used',
      createdAt: 'Created',
      neverUsed: 'Never Used',
      active: 'Active',
      inactive: 'Inactive',
      
      // Billing & Payments  
      billingPayments: 'Billing & Payments',
      currentBalance: 'Current Balance',
      addFunds: 'Add Funds',
      amount: 'Amount',
      topupWallet: 'Top Up Wallet',
      paymentSuccessful: 'Payment Successful',
      paymentFailed: 'Payment Failed',
      minimumAmount: 'Minimum: €0.50',
      maximumAmount: 'Maximum: €10,000.00',
      completePayment: 'Complete Payment',
      paymentDetails: 'Payment Details',
      amountToPay: 'Amount to pay',
      testCard: 'Test card: 4242 4242 4242 4242, any future expiry, any CVC',
      paymentComplete: 'Payment Complete',
      walletUpdated: 'Your wallet will be updated shortly',
      
      // Transaction History
      transactionHistory: 'Transaction History',
      date: 'Date',
      description: 'Description',
      type: 'Type',
      invoice: 'Invoice',
      payment: 'Payment',
      noTransactions: 'No transactions found',
      downloadInvoice: 'Download Invoice',
      
      // Usage & Statistics
      usageStatistics: 'Usage Statistics',
      totalCalls: 'Total API Calls',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      totalMinutes: 'Total Minutes Processed',
      averageCallDuration: 'Average Call Duration',
      mostUsedEngine: 'Most Used Engine',
      callsPerDay: 'Calls per Day',
      minutesPerDay: 'Minutes per Day',
      usageBreakdown: 'Usage Breakdown',
      byEngine: 'By Engine',
      byDate: 'By Date',
      
      // Missing keys from error logs
      usageAnalytics: 'Usage Analytics',
      timeRange: 'Time Range',
      days: 'days',
      
      // Wallet & Credits
      walletBalance: 'Wallet Balance',
      availableCredits: 'Available Credits',
      creditsUsed: 'Credits Used',
      creditTransaction: 'Credit Transaction',
      welcomeBonus: 'Welcome Bonus',
      stripePayment: 'Stripe Payment',
      
      // Errors & Validation
      invalidAmount: 'Please enter a valid amount',
      networkError: 'Network error occurred',
      paymentIntentFailed: 'Payment intent creation failed',
      billingDataError: 'Failed to load billing data',
      deleteKeyError: 'Failed to delete API key',
      createKeyError: 'Failed to create API key',
      rotateKeyError: 'Failed to rotate API key',
      
      // Developer Documentation
      developerDocs: 'Developer Documentation',
      developerDocsSubtitle: "Integrate Voxist's powerful speech recognition API into your applications",
      websocketSamples: 'WebSocket Samples',
      apiReference: 'API Reference',
      
      // Features
      realtimeProcessing: 'Real-time Processing',
      realtimeDescription: 'Stream audio and get instant transcription results with our WebSocket API',
      multiLanguageSupport: 'Multi-language Support',
      multiLanguageDescription: 'Support for French, English, German, Spanish, Italian, Portuguese, and more',
      secureReliable: 'Secure & Reliable',
      secureReliableDescription: 'Enterprise-grade security with API key authentication and encrypted connections',
      
      // Quick Start
      quickStartGuide: 'Quick Start Guide',
      getStartedMinutes: 'Get Started in Minutes',
      followStepsIntegrate: 'Follow these steps to integrate Voxist ASR into your application',
      getApiKey: 'Get Your API Key',
      getApiKeyDescription: 'Generate an API key from your developer portal dashboard. Click on the user menu and select "API Keys" to create a new key.',
      uploadAudioFiles: 'Upload Audio Files',
      uploadAudioDescription: 'Use our REST API to upload audio files for transcription. Supported formats: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Integrate Real-time',
      integrateRealtimeDescription: 'For real-time transcription, use our WebSocket API to stream audio and receive instant results.',
      supportedLanguages: 'Supported Languages',
      
      // Language codes
      frenchLanguage: 'French',
      frenchMedical: 'French (Medical)',
      englishLanguage: 'English',
      germanLanguage: 'German',
      spanishLanguage: 'Spanish',
      italianLanguage: 'Italian',
      portugueseLanguage: 'Portuguese',
      dutchLanguage: 'Dutch',
      swedishLanguage: 'Swedish',
      polishLanguage: 'Polish',
      japaneseLanguage: 'Japanese',
      hebrewLanguage: 'Hebrew',
      turkishLanguage: 'Turkish',

      // Tab Labels
      quickStart: 'Quick Start',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Authentication',
      
      // Examples descriptions
      curlExamplesTitle: 'cURL Examples',
      curlDescription: 'Use these cURL commands to test the API directly from your terminal.',
      nodeJsTitle: 'Node.js Integration',
      nodeJsDescription: 'Complete Node.js example using axios and form-data libraries.',
      pythonTitle: 'Python Integration',
      pythonDescription: 'Python example using the requests library for HTTP API calls.',
      websocketTitle: 'WebSocket Real-time Transcription',
      websocketDescription: 'For real-time transcription, use our WebSocket API to stream audio data and receive instant transcription results.',
      
      // WebSocket details
      completeWebsocketSamples: 'Complete WebSocket Samples Available',
      checkGithubRepo: 'Check out our GitHub repository for complete WebSocket examples in multiple languages',
      viewMoreExamples: 'View More Examples',
      websocketProtocol: 'WebSocket Protocol',
      connectionUrl: 'Connection URL',
      audioFormat: 'Audio Format',
      messageTypes: 'Message Types',
      audioFormatDetails: '16-bit PCM, 16kHz sample rate',
      messageTypesDetails: 'Send binary audio data, receive JSON text responses',
      
      // Authentication
      authenticationTitle: 'Authentication',
      apiKeyAuthentication: 'API Key Authentication',
      apiKeyUsage: 'Use your API key in the X-LVL-KEY header for HTTP requests:',
      jwtTokenAuthentication: 'JWT Token Authentication (WebSocket)',
      jwtDescription: 'For WebSocket connections, you need a JWT token. Get one using the OAuth endpoint:',
      securityBestPractices: 'Security Best Practices',
      securityTip1: 'Never expose your API keys in client-side code',
      securityTip2: 'Use environment variables to store credentials',
      securityTip3: 'Regularly rotate your API keys',
      securityTip4: 'Monitor your API usage from the developer dashboard',
      
      // Footer
      needHelp: 'Need help? Contact our support team or check out our',
      githubSamples: 'GitHub samples',
      forMoreExamples: 'for more examples.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Admin Panel',
      billingManagement: 'Billing Management',
      walletCredits: 'Wallet Credits',
      customPricing: 'Custom Pricing',

      // Billing Tab
      userBillingSettings: 'User Billing Settings',
      billingSettingsDescription: 'Enable or disable automatic monthly invoicing for users and set their invoice day.',
      user: 'User',
      email: 'Email',
      stripeCustomer: 'Stripe Customer',
      autoInvoicing: 'Auto-Invoicing',
      invoiceDay: 'Invoice Day',
      lastInvoice: 'Last Invoice',
      actions: 'Actions',
      edit: 'Edit',

      // Credits Tab
      addWalletCredits: 'Add Wallet Credits',
      creditsDescription: 'Add credits to user wallets. This is for administrative adjustments only.',
      selectUser: 'Select User',
      reason: 'Reason',
      creditReasonPlaceholder: 'e.g., Promotional credit, refund, etc.',
      addCredit: 'Add Credit',
      adding: 'Adding...',

      // Pricing Tab
      customUserPricing: 'Custom User Pricing',
      pricingDescription: 'Set custom transcription prices per user per engine. Prices are in microeuros per second.',
      engine: 'Engine',
      defaultPrice: 'Default Price',
      currentPrice: 'Current Price',
      newPrice: 'New Price',
      status: 'Status',
      custom: 'Custom',
      default: 'Default',
      noChanges: 'No changes',
      refresh: 'Refresh',
      noEnginesAvailable: 'No engines available',
      selectUserToViewPricing: 'Select a user to view and edit their custom pricing',

      // TTS
      'tts.title': 'Text to Speech',
      'tts.subtitle': 'Convert text to natural-sounding speech',
      'tts.voiceLabel': 'Voice',
      'tts.loadingVoices': 'Loading voices...',
      'tts.voicesError': 'Failed to load voices',
      'tts.textLabel': 'Text to synthesize',
      'tts.textPlaceholder': 'Enter text here...',
      'tts.generateButton': 'Generate Speech',
      'tts.generating': 'Generating...',
      'tts.errorGeneric': 'Failed to generate audio',
      'tts.error402': 'Insufficient balance. Please top up your wallet via the billing menu to use TTS.',
      'tts.sampleTexts': 'Sample Texts',
      'tts.streamingMode': 'Streaming mode',
      'tts.selectVoiceFirst': 'Please select a voice to start streaming',
      'tts.connecting': 'Connecting...',
      'tts.connected': 'Connected',
      'tts.streaming': 'Streaming...',
      'tts.disconnected': 'Disconnected',
      'tts.stopStreaming': 'Stop',
      'tts.streamingError': 'Streaming error',
      'tts.downloadAudio': 'Download audio',
      'tts.play': 'Play',
      'tts.pause': 'Pause',
      'tts.performanceMetrics': 'Performance Metrics',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Total Time',
      'tts.characters': 'Characters',
      'tts.audioDuration': 'Audio Duration',
      'tts.estimatedCost': 'Est. Cost',
      'tts.requestId': 'Request ID',
      voiceGallery: 'Voice Gallery',
      voiceGallerySubtitle: 'Browse, preview, and clone voices',
      stockVoices: 'Stock Voices',
      myClonedVoices: 'My Cloned Voices',
      addNewVoice: 'Add New Voice',
      cloneVoice: 'Clone Voice',
      voiceName: 'Voice Name',
      voiceDescription: 'Description',
      uploadWavFiles: 'Upload WAV Files',
      dragDropWav: 'Drag and drop WAV files here, or click to browse',
      cloning: 'Cloning...',
      cloneSuccess: 'Voice cloned successfully',
      deleteVoice: 'Delete Voice',
      confirmDeleteVoice: 'Are you sure you want to delete this voice? This cannot be undone.',
      voiceDeleted: 'Voice deleted',
      noClonedVoices: 'No cloned voices yet. Create your first custom voice!',
      searchVoices: 'Search voices...',
      filterByLanguage: 'Language',
      allLanguages: 'All Languages',
      allTypes: 'All',
      stock: 'Stock',
      cloned: 'Cloned',
      tryVoice: 'Try it',
      preview: 'Preview',
      ttsQuickStart: 'TTS Quick Start',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Text-to-Speech Overview',
      ttsOverviewDescription: 'Convert text into natural-sounding speech using our TTS API.',
      ttsStep1: 'Get Your API Key',
      ttsStep2: 'List Available Voices',
      ttsStep3: 'Synthesize Speech',
      ttsSupportedFormats: 'Supported output formats: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'TTS REST API Reference',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Stream text-to-speech audio in real-time using WebSocket connections.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Total Characters',
      ttsUsageNote: 'TTS usage tracking will be available soon. Your TTS costs appear in Billing transaction history.',
      ttsCost: 'TTS Cost',
      ttsThisMonth: 'This Month',
      avgTtfb: 'Avg TTFB',
      sharedBalance: 'Shared balance for ASR and TTS services',
      pricing: 'Pricing',
      asrPricing: 'ASR - Speech to Text',
      ttsPricing: 'TTS - Text to Speech',
      perMinuteRate: '€0.01 per minute of audio',
      standardVoiceRate: '€0.01 per 1,000 characters (standard)',
      clonedVoiceRate: '€0.03 per 1,000 characters (cloned)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Record speech, transcribe, edit, and re-synthesize',
      'tts.recordOrUpload': 'Record or Upload Audio',
      'tts.recordAudio': 'Record',
      'tts.uploadFile': 'Upload File',
      'tts.transcribing': 'Transcribing...',
      'tts.transcriptionComplete': 'Transcription complete',
      'tts.editTranscription': 'Edit Transcription',
      'tts.proceedToTts': 'Proceed to TTS',
      'tts.speakIt': 'Speak it',
      'tts.originalRecording': 'Original Recording',
      'tts.ttsOutput': 'TTS Output',
      'tts.step1Record': 'Record',
      'tts.step2Transcribe': 'Transcribe',
      'tts.step3Edit': 'Edit',
      'tts.step4Speak': 'Speak',
      'tts.startOver': 'Start Over',
      'tts.compareAudio': 'Compare Audio',
      'tts.or': 'or',

      // Form page audio controls
      'form.playAudio': 'Play Audio',
      'form.pauseAudio': 'Pause Audio',

      // ResultBox
      'result.providerResult': 'Result',
      'result.decreaseText': 'Decrease Text',
      'result.increaseText': 'Increase Text',
      'result.copyResult': 'Copy result to clipboard',

      // Reset password
      'reset.title': 'Reset your password',
      'reset.subtitle': 'Enter your new password',
      'reset.newPassword': 'New password',
      'reset.confirmation': 'Confirmation',
      'reset.submit': 'Reset',

      // Send reset
      'sendReset.title': 'Forgot password?',
      'sendReset.description': "Please enter your email address, we'll send you instructions to help reset your password",
      'sendReset.submit': 'Send reset instructions',

      // Teleprompter route
      'teleprompter.start': 'Start',
      'teleprompter.stop': 'Stop',

      // TeleprompterOverlay controls
      'teleprompter.settings': 'Settings',
      'teleprompter.fullscreen': 'Fullscreen (F)',
      'teleprompter.exitFullscreen': 'Exit Fullscreen (F)',
      'teleprompter.close': 'Close (Esc)',
      'teleprompter.fontSize': 'Font Size',
      'teleprompter.sizeSmall': 'Small',
      'teleprompter.sizeNormal': 'Normal',
      'teleprompter.sizeLarge': 'Large',
      'teleprompter.sizeXL': 'XL',
      'teleprompter.recording': 'Recording',
      'teleprompter.ready': 'Ready',
      'teleprompter.shortcuts': 'F: Fullscreen • +/-: Font Size • Esc: Exit'
    }
  },
  fr: {
    translation: {
      // App title
      appTitle: 'Portail Développeur Voxist AI',
      tagline: 'Plateforme IA Vocale - ASR & TTS',
      
      // Login form
      welcomeBack: 'Bonjour !',
      signInPrompt: 'Connectez-vous à votre compte développeur',
      signInWithEmail: 'Se connecter avec Email',
      signInWithGoogle: 'Se connecter avec Google',
      signInWithMicrosoft: 'Se connecter avec Microsoft',
      
      // User menu
      userMenu: 'Menu utilisateur',
      billing: 'Facturation',
      usage: 'Utilisation',
      apiKeys: 'Clés API',
      signOut: 'Déconnexion',
      
      // Language selector
      selectLanguage: 'Sélectionner la langue',
      
      // Common
      close: 'Fermer',
      cancel: 'Annuler',
      save: 'Enregistrer',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      
      // API Keys
      apiKeyManagement: 'Gestion des Clés API',
      createApiKey: 'Créer une Clé API',
      apiKeyName: 'Nom de la Clé API',
      apiKeyDescription: 'Description',
      generateKey: 'Générer la Clé',
      copyKey: 'Copier la Clé',
      keyCreated: 'Clé API Créée',
      keyRotated: 'Clé API Renouvelée',
      rotateKey: 'Renouveler la Clé',
      deleteKey: 'Supprimer la Clé',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer cette clé API ?',
      keyDeleted: 'Clé API Supprimée',
      showKey: 'Afficher la Clé',
      hideKey: 'Masquer la Clé',
      lastUsed: 'Dernière Utilisation',
      createdAt: 'Créée le',
      neverUsed: 'Jamais Utilisée',
      active: 'Active',
      inactive: 'Inactive',
      
      // Billing & Payments  
      billingPayments: 'Facturation et Paiements',
      currentBalance: 'Solde Actuel',
      addFunds: 'Ajouter des Fonds',
      amount: 'Montant',
      topupWallet: 'Recharger le Portefeuille',
      paymentSuccessful: 'Paiement Réussi',
      paymentFailed: 'Paiement Échoué',
      minimumAmount: 'Minimum : 0,50 €',
      maximumAmount: 'Maximum : 10 000,00 €',
      completePayment: 'Finaliser le Paiement',
      paymentDetails: 'Détails du Paiement',
      amountToPay: 'Montant à payer',
      testCard: "Carte de test : 4242 4242 4242 4242, n'importe quelle date future, n'importe quel CVC",
      paymentComplete: 'Paiement Terminé',
      walletUpdated: 'Votre portefeuille sera mis à jour sous peu',
      
      // Transaction History
      transactionHistory: 'Historique des Transactions',
      date: 'Date',
      description: 'Description',
      type: 'Type',
      invoice: 'Facture',
      payment: 'Paiement',
      noTransactions: 'Aucune transaction trouvée',
      downloadInvoice: 'Télécharger la Facture',
      
      // Usage & Statistics
      usageStatistics: "Statistiques d'Utilisation",
      totalCalls: 'Total des Appels API',
      thisMonth: 'Ce Mois-ci',
      lastMonth: 'Mois Dernier',
      totalMinutes: 'Total des Minutes Traitées',
      averageCallDuration: "Durée Moyenne d'Appel",
      mostUsedEngine: 'Moteur le Plus Utilisé',
      callsPerDay: 'Appels par Jour',
      minutesPerDay: 'Minutes par Jour',
      usageBreakdown: "Répartition de l'Utilisation",
      byEngine: 'Par Moteur',
      byDate: 'Par Date',
      
      // Missing keys from error logs
      usageAnalytics: "Analyses d'utilisation",
      timeRange: 'Période',
      days: 'jours',
      
      // Wallet & Credits
      walletBalance: 'Solde du Portefeuille',
      availableCredits: 'Crédits Disponibles',
      creditsUsed: 'Crédits Utilisés',
      creditTransaction: 'Transaction de Crédit',
      welcomeBonus: 'Bonus de Bienvenue',
      stripePayment: 'Paiement Stripe',
      
      // Errors & Validation
      invalidAmount: 'Veuillez saisir un montant valide',
      networkError: 'Erreur réseau survenue',
      paymentIntentFailed: "Échec de la création de l'intention de paiement",
      billingDataError: 'Impossible de charger les données de facturation',
      deleteKeyError: 'Impossible de supprimer la clé API',
      createKeyError: 'Impossible de créer la clé API',
      rotateKeyError: 'Impossible de renouveler la clé API',
      
      // Developer Documentation
      developerDocs: 'Documentation Développeur',
      developerDocsSubtitle: 'Intégrez la puissante API de reconnaissance vocale de Voxist dans vos applications',
      websocketSamples: 'Exemples WebSocket',
      apiReference: 'Référence API',
      
      // Features
      realtimeProcessing: 'Traitement en Temps Réel',
      realtimeDescription: "Diffusez de l'audio et obtenez des résultats de transcription instantanés avec notre API WebSocket",
      multiLanguageSupport: 'Support Multi-langues',
      multiLanguageDescription: "Support pour le français, l'anglais, l'allemand, l'espagnol, l'italien, le portugais, et plus",
      secureReliable: 'Sécurisé et Fiable',
      secureReliableDescription: 'Sécurité de niveau entreprise avec authentification par clé API et connexions chiffrées',
      
      // Quick Start
      quickStartGuide: 'Guide de Démarrage Rapide',
      getStartedMinutes: 'Commencez en Quelques Minutes',
      followStepsIntegrate: 'Suivez ces étapes pour intégrer Voxist ASR dans votre application',
      getApiKey: 'Obtenez Votre Clé API',
      getApiKeyDescription: 'Générez une clé API depuis votre tableau de bord développeur. Cliquez sur le menu utilisateur et sélectionnez "Clés API" pour créer une nouvelle clé.',
      uploadAudioFiles: 'Téléchargez des Fichiers Audio',
      uploadAudioDescription: 'Utilisez notre API REST pour télécharger des fichiers audio pour la transcription. Formats supportés : WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Intégrez le Temps Réel',
      integrateRealtimeDescription: "Pour la transcription en temps réel, utilisez notre API WebSocket pour diffuser de l'audio et recevoir des résultats instantanés.",
      supportedLanguages: 'Langues Supportées',
      
      // Language codes
      frenchLanguage: 'Français',
      frenchMedical: 'Français (Médical)',
      englishLanguage: 'Anglais',
      germanLanguage: 'Allemand',
      spanishLanguage: 'Espagnol',
      italianLanguage: 'Italien',
      portugueseLanguage: 'Portugais',
      dutchLanguage: 'Néerlandais',
      
      // Tab Labels
      quickStart: 'Démarrage Rapide',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Authentification',
      
      // Examples descriptions
      curlExamplesTitle: 'Exemples cURL',
      curlDescription: "Utilisez ces commandes cURL pour tester l'API directement depuis votre terminal.",
      nodeJsTitle: 'Intégration Node.js',
      nodeJsDescription: 'Exemple complet Node.js utilisant les bibliothèques axios et form-data.',
      pythonTitle: 'Intégration Python',
      pythonDescription: "Exemple Python utilisant la bibliothèque requests pour les appels d'API HTTP.",
      websocketTitle: 'Transcription WebSocket en Temps Réel',
      websocketDescription: 'Pour la transcription en temps réel, utilisez notre API WebSocket pour diffuser des données audio et recevoir des résultats de transcription instantanés.',
      
      // WebSocket details
      completeWebsocketSamples: 'Exemples WebSocket Complets Disponibles',
      checkGithubRepo: 'Consultez notre dépôt GitHub pour des exemples WebSocket complets dans plusieurs langages',
      viewMoreExamples: "Voir Plus d'Exemples",
      websocketProtocol: 'Protocole WebSocket',
      connectionUrl: 'URL de Connexion',
      audioFormat: 'Format Audio',
      messageTypes: 'Types de Messages',
      audioFormatDetails: "PCM 16-bit, fréquence d'échantillonnage 16kHz",
      messageTypesDetails: 'Envoyez des données audio binaires, recevez des réponses texte JSON',
      
      // Authentication
      authenticationTitle: 'Authentification',
      apiKeyAuthentication: 'Authentification par Clé API',
      apiKeyUsage: "Utilisez votre clé API dans l'en-tête X-LVL-KEY pour les requêtes HTTP :",
      jwtTokenAuthentication: 'Authentification par Token JWT (WebSocket)',
      jwtDescription: "Pour les connexions WebSocket, vous avez besoin d'un token JWT. Obtenez-en un en utilisant l'endpoint OAuth :",
      securityBestPractices: 'Meilleures Pratiques de Sécurité',
      securityTip1: "N'exposez jamais vos clés API dans le code côté client",
      securityTip2: "Utilisez des variables d'environnement pour stocker les identifiants",
      securityTip3: 'Renouvelez régulièrement vos clés API',
      securityTip4: "Surveillez votre utilisation de l'API depuis le tableau de bord développeur",
      
      // Footer
      needHelp: "Besoin d'aide ? Contactez notre équipe de support ou consultez nos",
      githubSamples: 'exemples GitHub',
      forMoreExamples: "pour plus d'exemples.",

      // Admin Panel
      admin: 'Admin',
      adminPanel: "Panneau d'Administration",
      billingManagement: 'Gestion de la Facturation',
      walletCredits: 'Crédits Portefeuille',
      customPricing: 'Tarification Personnalisée',

      // Billing Tab
      userBillingSettings: 'Paramètres de Facturation Utilisateurs',
      billingSettingsDescription: 'Activez ou désactivez la facturation automatique mensuelle pour les utilisateurs et définissez leur jour de facturation.',
      user: 'Utilisateur',
      email: 'Email',
      stripeCustomer: 'Client Stripe',
      autoInvoicing: 'Facturation Auto',
      invoiceDay: 'Jour de Facture',
      lastInvoice: 'Dernière Facture',
      actions: 'Actions',
      edit: 'Modifier',

      // Credits Tab
      addWalletCredits: 'Ajouter des Crédits',
      creditsDescription: 'Ajoutez des crédits aux portefeuilles utilisateurs. Ceci est réservé aux ajustements administratifs.',
      selectUser: 'Sélectionner un Utilisateur',
      reason: 'Raison',
      creditReasonPlaceholder: 'ex: Crédit promotionnel, remboursement, etc.',
      addCredit: 'Ajouter Crédit',
      adding: 'Ajout en cours...',

      // Pricing Tab
      customUserPricing: 'Tarification Personnalisée par Utilisateur',
      pricingDescription: 'Définissez des prix de transcription personnalisés par utilisateur et par moteur. Les prix sont en microeuros par seconde.',
      engine: 'Moteur',
      defaultPrice: 'Prix par Défaut',
      currentPrice: 'Prix Actuel',
      newPrice: 'Nouveau Prix',
      status: 'Statut',
      custom: 'Personnalisé',
      default: 'Par Défaut',
      noChanges: 'Pas de modifications',
      refresh: 'Actualiser',
      noEnginesAvailable: 'Aucun moteur disponible',
      selectUserToViewPricing: 'Sélectionnez un utilisateur pour voir et modifier sa tarification personnalisée',

      // TTS
      'tts.title': 'Synthèse Vocale',
      'tts.subtitle': 'Convertir le texte en parole naturelle',
      'tts.voiceLabel': 'Voix',
      'tts.loadingVoices': 'Chargement des voix...',
      'tts.voicesError': 'Échec du chargement des voix',
      'tts.textLabel': 'Texte à synthétiser',
      'tts.textPlaceholder': 'Entrez le texte ici...',
      'tts.generateButton': 'Générer la Parole',
      'tts.generating': 'Génération...',
      'tts.errorGeneric': 'Échec de la génération audio',
      'tts.error402': 'Solde insuffisant. Veuillez recharger votre portefeuille via le menu de facturation pour utiliser la synthèse vocale.',
      'tts.sampleTexts': "Textes d'Exemple",
      'tts.streamingMode': 'Mode streaming',
      'tts.selectVoiceFirst': 'Veuillez sélectionner une voix pour commencer le streaming',
      'tts.connecting': 'Connexion...',
      'tts.connected': 'Connecté',
      'tts.streaming': 'En streaming...',
      'tts.disconnected': 'Déconnecté',
      'tts.stopStreaming': 'Arrêter',
      'tts.streamingError': 'Erreur de streaming',
      'tts.downloadAudio': "Télécharger l'audio",
      'tts.play': 'Lire',
      'tts.pause': 'Pause',
      'tts.performanceMetrics': 'Métriques de Performance',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Temps Total',
      'tts.characters': 'Caractères',
      'tts.audioDuration': 'Durée Audio',
      'tts.estimatedCost': 'Coût Est.',
      'tts.requestId': 'ID de Requête',
      voiceGallery: 'Galerie de Voix',
      voiceGallerySubtitle: 'Parcourir, prévisualiser et cloner des voix',
      stockVoices: 'Voix Standard',
      myClonedVoices: 'Mes Voix Clonées',
      addNewVoice: 'Ajouter une Voix',
      cloneVoice: 'Cloner la Voix',
      voiceName: 'Nom de la Voix',
      voiceDescription: 'Description',
      uploadWavFiles: 'Télécharger des Fichiers WAV',
      dragDropWav: 'Glissez-déposez des fichiers WAV ici, ou cliquez pour parcourir',
      cloning: 'Clonage...',
      cloneSuccess: 'Voix clonée avec succès',
      deleteVoice: 'Supprimer la Voix',
      confirmDeleteVoice: 'Êtes-vous sûr de vouloir supprimer cette voix ? Cette action est irréversible.',
      voiceDeleted: 'Voix supprimée',
      noClonedVoices: "Aucune voix clonée pour l'instant. Créez votre première voix personnalisée !",
      searchVoices: 'Rechercher des voix...',
      filterByLanguage: 'Langue',
      allLanguages: 'Toutes les Langues',
      allTypes: 'Tous',
      stock: 'Standard',
      cloned: 'Clonée',
      tryVoice: 'Essayer',
      preview: 'Aperçu',
      ttsQuickStart: 'Démarrage Rapide TTS',
      ttsRestApi: 'API REST TTS',
      ttsWebSocket: 'WebSocket TTS',
      ttsOverview: "Vue d'ensemble de la Synthèse Vocale",
      ttsOverviewDescription: 'Convertissez le texte en parole naturelle grâce à notre API TTS.',
      ttsStep1: 'Obtenez Votre Clé API',
      ttsStep2: 'Listez les Voix Disponibles',
      ttsStep3: 'Synthétisez la Parole',
      ttsSupportedFormats: 'Formats de sortie supportés : wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'Référence API REST TTS',
      ttsWsTitle: 'Streaming WebSocket TTS',
      ttsWsDescription: "Diffusez l'audio de synthèse vocale en temps réel via des connexions WebSocket.",
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Total Caractères',
      ttsUsageNote: "Le suivi de l'utilisation TTS sera disponible prochainement. Vos coûts TTS apparaissent dans l'historique des transactions de facturation.",
      ttsCost: 'Coût TTS',
      ttsThisMonth: 'Ce Mois',
      avgTtfb: 'TTFB Moy.',
      sharedBalance: 'Solde partagé pour les services ASR et TTS',
      pricing: 'Tarification',
      asrPricing: 'ASR - Reconnaissance Vocale',
      ttsPricing: 'TTS - Synthèse Vocale',
      perMinuteRate: "€0,01 par minute d'audio",
      standardVoiceRate: '€0,01 pour 1 000 caractères (standard)',
      clonedVoiceRate: '€0,03 pour 1 000 caractères (clonée)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Enregistrer la parole, transcrire, modifier et re-synthétiser',
      'tts.recordOrUpload': 'Enregistrer ou Télécharger un Audio',
      'tts.recordAudio': 'Enregistrer',
      'tts.uploadFile': 'Télécharger un Fichier',
      'tts.transcribing': 'Transcription en cours...',
      'tts.transcriptionComplete': 'Transcription terminée',
      'tts.editTranscription': 'Modifier la Transcription',
      'tts.proceedToTts': 'Passer au TTS',
      'tts.speakIt': 'Synthétiser',
      'tts.originalRecording': 'Enregistrement Original',
      'tts.ttsOutput': 'Sortie TTS',
      'tts.step1Record': 'Enregistrer',
      'tts.step2Transcribe': 'Transcrire',
      'tts.step3Edit': 'Modifier',
      'tts.step4Speak': 'Synthétiser',
      'tts.startOver': 'Recommencer',
      'tts.compareAudio': "Comparer l'Audio",
      'tts.or': 'ou',

      // Form page audio controls
      'form.playAudio': "Lire l'audio",
      'form.pauseAudio': 'Mettre en pause',

      // ResultBox
      'result.providerResult': 'Résultat',
      'result.decreaseText': 'Réduire le texte',
      'result.increaseText': 'Agrandir le texte',
      'result.copyResult': 'Copier le résultat',

      // Reset password
      'reset.title': 'Réinitialiser votre mot de passe',
      'reset.subtitle': 'Entrez votre nouveau mot de passe',
      'reset.newPassword': 'Nouveau mot de passe',
      'reset.confirmation': 'Confirmation',
      'reset.submit': 'Réinitialiser',

      // Send reset
      'sendReset.title': 'Mot de passe oublié ?',
      'sendReset.description': 'Veuillez entrer votre adresse e-mail, nous vous enverrons des instructions pour réinitialiser votre mot de passe',
      'sendReset.submit': 'Envoyer les instructions',

      // Teleprompter route
      'teleprompter.start': 'Démarrer',
      'teleprompter.stop': 'Arrêter',

      // TeleprompterOverlay controls
      'teleprompter.settings': 'Paramètres',
      'teleprompter.fullscreen': 'Plein écran (F)',
      'teleprompter.exitFullscreen': 'Quitter le plein écran (F)',
      'teleprompter.close': 'Fermer (Échap)',
      'teleprompter.fontSize': 'Taille de police',
      'teleprompter.sizeSmall': 'Petit',
      'teleprompter.sizeNormal': 'Normal',
      'teleprompter.sizeLarge': 'Grand',
      'teleprompter.sizeXL': 'XL',
      'teleprompter.recording': 'Enregistrement',
      'teleprompter.ready': 'Prêt',
      'teleprompter.shortcuts': 'F : Plein écran • +/- : Taille police • Échap : Quitter'
    }
  },
  de: {
    translation: {
      // App title
      appTitle: 'Voxist AI Entwicklerportal',
      tagline: 'KI-Sprachplattform - ASR & TTS',
      
      // Login form
      welcomeBack: 'Willkommen zurück',
      signInPrompt: 'Melden Sie sich bei Ihrem Entwicklerkonto an',
      signInWithEmail: 'Mit E-Mail anmelden',
      signInWithGoogle: 'Mit Google anmelden',
      signInWithMicrosoft: 'Mit Microsoft anmelden',
      
      // User menu
      userMenu: 'Benutzermenü',
      billing: 'Abrechnung',
      usage: 'Nutzung',
      apiKeys: 'API-Schlüssel',
      signOut: 'Abmelden',
      
      // Language selector
      selectLanguage: 'Sprache auswählen',
      
      // Common
      close: 'Schließen',
      cancel: 'Abbrechen',
      save: 'Speichern',
      loading: 'Laden...',
      error: 'Fehler',
      success: 'Erfolg',
      
      // API Keys
      apiKeyManagement: 'API-Schlüssel-Verwaltung',
      createApiKey: 'API-Schlüssel Erstellen',
      apiKeyName: 'API-Schlüssel Name',
      apiKeyDescription: 'Beschreibung',
      generateKey: 'Schlüssel Generieren',
      copyKey: 'Schlüssel Kopieren',
      keyCreated: 'API-Schlüssel Erstellt',
      keyRotated: 'API-Schlüssel Rotiert',
      rotateKey: 'Schlüssel Rotieren',
      deleteKey: 'Schlüssel Löschen',
      confirmDelete: 'Sind Sie sicher, dass Sie diesen API-Schlüssel löschen möchten?',
      keyDeleted: 'API-Schlüssel Gelöscht',
      showKey: 'Schlüssel Anzeigen',
      hideKey: 'Schlüssel Verbergen',
      lastUsed: 'Zuletzt Verwendet',
      createdAt: 'Erstellt',
      neverUsed: 'Nie Verwendet',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      
      // Billing & Payments  
      billingPayments: 'Abrechnung & Zahlungen',
      currentBalance: 'Aktuelles Guthaben',
      addFunds: 'Guthaben Aufladen',
      amount: 'Betrag',
      topupWallet: 'Wallet Aufladen',
      paymentSuccessful: 'Zahlung Erfolgreich',
      paymentFailed: 'Zahlung Fehlgeschlagen',
      minimumAmount: 'Minimum: €0,50',
      maximumAmount: 'Maximum: €10.000,00',
      completePayment: 'Zahlung Abschließen',
      paymentDetails: 'Zahlungsdetails',
      amountToPay: 'Zu zahlender Betrag',
      testCard: 'Testkarte: 4242 4242 4242 4242, beliebiges zukünftiges Datum, beliebiger CVC',
      paymentComplete: 'Zahlung Abgeschlossen',
      walletUpdated: 'Ihre Wallet wird in Kürze aktualisiert',
      
      // Transaction History
      transactionHistory: 'Transaktionshistorie',
      date: 'Datum',
      description: 'Beschreibung',
      type: 'Typ',
      invoice: 'Rechnung',
      payment: 'Zahlung',
      noTransactions: 'Keine Transaktionen gefunden',
      downloadInvoice: 'Rechnung Herunterladen',
      
      // Usage & Statistics
      usageStatistics: 'Nutzungsstatistiken',
      totalCalls: 'Gesamt API-Aufrufe',
      thisMonth: 'Diesen Monat',
      lastMonth: 'Letzten Monat',
      totalMinutes: 'Gesamt Verarbeitete Minuten',
      averageCallDuration: 'Durchschnittliche Anrufdauer',
      mostUsedEngine: 'Am Häufigsten Verwendete Engine',
      callsPerDay: 'Anrufe pro Tag',
      minutesPerDay: 'Minuten pro Tag',
      usageBreakdown: 'Nutzungsaufschlüsselung',
      byEngine: 'Nach Engine',
      byDate: 'Nach Datum',
      
      // Missing keys from error logs
      usageAnalytics: 'Nutzungsanalyse',
      timeRange: 'Zeitraum', 
      days: 'Tage',
      
      // Wallet & Credits
      walletBalance: 'Wallet-Guthaben',
      availableCredits: 'Verfügbare Credits',
      creditsUsed: 'Verwendete Credits',
      creditTransaction: 'Credit-Transaktion',
      welcomeBonus: 'Willkommensbonus',
      stripePayment: 'Stripe-Zahlung',
      
      // Errors & Validation
      invalidAmount: 'Bitte geben Sie einen gültigen Betrag ein',
      networkError: 'Netzwerkfehler aufgetreten',
      paymentIntentFailed: 'Zahlungsabsicht-Erstellung fehlgeschlagen',
      billingDataError: 'Abrechnungsdaten konnten nicht geladen werden',
      deleteKeyError: 'API-Schlüssel konnte nicht gelöscht werden',
      createKeyError: 'API-Schlüssel konnte nicht erstellt werden',
      rotateKeyError: 'API-Schlüssel konnte nicht rotiert werden',
      
      // Developer Documentation
      developerDocs: 'Entwicklerdokumentation',
      developerDocsSubtitle: 'Integrieren Sie Voxists leistungsstarke Spracherkennungs-API in Ihre Anwendungen',
      websocketSamples: 'WebSocket-Beispiele',
      apiReference: 'API-Referenz',
      
      // Features
      realtimeProcessing: 'Echtzeitverarbeitung',
      realtimeDescription: 'Streamen Sie Audio und erhalten Sie sofortige Transkriptionsergebnisse mit unserer WebSocket-API',
      multiLanguageSupport: 'Mehrsprachiger Support',
      multiLanguageDescription: 'Unterstützung für Deutsch, Englisch, Französisch, Spanisch, Italienisch, Portugiesisch und mehr',
      secureReliable: 'Sicher und Zuverlässig',
      secureReliableDescription: 'Unternehmensklasse-Sicherheit mit API-Schlüssel-Authentifizierung und verschlüsselten Verbindungen',
      
      // Quick Start
      quickStartGuide: 'Schnellstart-Anleitung',
      getStartedMinutes: 'In Minuten Loslegen',
      followStepsIntegrate: 'Befolgen Sie diese Schritte, um Voxist ASR in Ihre Anwendung zu integrieren',
      getApiKey: 'Holen Sie sich Ihren API-Schlüssel',
      getApiKeyDescription: 'Generieren Sie einen API-Schlüssel aus Ihrem Entwickler-Portal-Dashboard. Klicken Sie auf das Benutzermenü und wählen Sie "API-Schlüssel", um einen neuen Schlüssel zu erstellen.',
      uploadAudioFiles: 'Audio-Dateien Hochladen',
      uploadAudioDescription: 'Verwenden Sie unsere REST-API, um Audio-Dateien für die Transkription hochzuladen. Unterstützte Formate: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Echtzeit Integrieren',
      integrateRealtimeDescription: 'Für Echtzeit-Transkription verwenden Sie unsere WebSocket-API, um Audio zu streamen und sofortige Ergebnisse zu erhalten.',
      supportedLanguages: 'Unterstützte Sprachen',
      
      // Language codes
      frenchLanguage: 'Französisch',
      frenchMedical: 'Französisch (Medizinisch)',
      englishLanguage: 'Englisch',
      germanLanguage: 'Deutsch',
      spanishLanguage: 'Spanisch',
      italianLanguage: 'Italienisch',
      portugueseLanguage: 'Portugiesisch',
      dutchLanguage: 'Niederländisch',
      
      // Tab Labels
      quickStart: 'Schnellstart',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Authentifizierung',
      
      // Examples descriptions
      curlExamplesTitle: 'cURL-Beispiele',
      curlDescription: 'Verwenden Sie diese cURL-Befehle, um die API direkt von Ihrem Terminal aus zu testen.',
      nodeJsTitle: 'Node.js-Integration',
      nodeJsDescription: 'Vollständiges Node.js-Beispiel mit axios- und form-data-Bibliotheken.',
      pythonTitle: 'Python-Integration',
      pythonDescription: 'Python-Beispiel mit der requests-Bibliothek für HTTP-API-Aufrufe.',
      websocketTitle: 'WebSocket-Echtzeit-Transkription',
      websocketDescription: 'Für Echtzeit-Transkription verwenden Sie unsere WebSocket-API, um Audio-Daten zu streamen und sofortige Transkriptionsergebnisse zu erhalten.',
      
      // WebSocket details
      completeWebsocketSamples: 'Vollständige WebSocket-Beispiele Verfügbar',
      checkGithubRepo: 'Schauen Sie sich unser GitHub-Repository für vollständige WebSocket-Beispiele in mehreren Sprachen an',
      viewMoreExamples: 'Weitere Beispiele Anzeigen',
      websocketProtocol: 'WebSocket-Protokoll',
      connectionUrl: 'Verbindungs-URL',
      audioFormat: 'Audio-Format',
      messageTypes: 'Nachrichtentypen',
      audioFormatDetails: '16-Bit PCM, 16kHz Abtastrate',
      messageTypesDetails: 'Binäre Audio-Daten senden, JSON-Text-Antworten erhalten',
      
      // Authentication
      authenticationTitle: 'Authentifizierung',
      apiKeyAuthentication: 'API-Schlüssel-Authentifizierung',
      apiKeyUsage: 'Verwenden Sie Ihren API-Schlüssel im X-LVL-KEY-Header für HTTP-Anfragen:',
      jwtTokenAuthentication: 'JWT-Token-Authentifizierung (WebSocket)',
      jwtDescription: 'Für WebSocket-Verbindungen benötigen Sie einen JWT-Token. Holen Sie sich einen über den OAuth-Endpunkt:',
      securityBestPractices: 'Sicherheits-Best-Practices',
      securityTip1: 'Niemals API-Schlüssel in clientseitigem Code preisgeben',
      securityTip2: 'Umgebungsvariablen zum Speichern von Anmeldedaten verwenden',
      securityTip3: 'API-Schlüssel regelmäßig rotieren',
      securityTip4: 'API-Nutzung über das Entwickler-Dashboard überwachen',
      
      // Footer
      needHelp: 'Brauchen Sie Hilfe? Kontaktieren Sie unser Support-Team oder schauen Sie sich unsere',
      githubSamples: 'GitHub-Beispiele',
      forMoreExamples: 'für weitere Beispiele an.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Administrationsbereich',
      billingManagement: 'Abrechnungsverwaltung',
      walletCredits: 'Wallet-Guthaben',
      customPricing: 'Individuelle Preise',

      // Billing Tab
      userBillingSettings: 'Benutzer-Abrechnungseinstellungen',
      billingSettingsDescription: 'Aktivieren oder deaktivieren Sie die automatische monatliche Rechnungsstellung für Benutzer und legen Sie ihren Rechnungstag fest.',
      user: 'Benutzer',
      email: 'E-Mail',
      stripeCustomer: 'Stripe-Kunde',
      autoInvoicing: 'Auto-Rechnung',
      invoiceDay: 'Rechnungstag',
      lastInvoice: 'Letzte Rechnung',
      actions: 'Aktionen',
      edit: 'Bearbeiten',

      // Credits Tab
      addWalletCredits: 'Wallet-Guthaben hinzufügen',
      creditsDescription: 'Fügen Sie Guthaben zu Benutzer-Wallets hinzu. Dies ist nur für administrative Anpassungen.',
      selectUser: 'Benutzer auswählen',
      reason: 'Grund',
      creditReasonPlaceholder: 'z.B. Werbeguthaben, Rückerstattung, etc.',
      addCredit: 'Guthaben hinzufügen',
      adding: 'Wird hinzugefügt...',

      // Pricing Tab
      customUserPricing: 'Individuelle Benutzerpreise',
      pricingDescription: 'Legen Sie individuelle Transkriptionspreise pro Benutzer und Engine fest. Preise sind in Mikroeuros pro Sekunde.',
      engine: 'Engine',
      defaultPrice: 'Standardpreis',
      currentPrice: 'Aktueller Preis',
      newPrice: 'Neuer Preis',
      status: 'Status',
      custom: 'Individuell',
      default: 'Standard',
      noChanges: 'Keine Änderungen',
      refresh: 'Aktualisieren',
      noEnginesAvailable: 'Keine Engines verfügbar',
      selectUserToViewPricing: 'Wählen Sie einen Benutzer aus, um seine individuellen Preise anzuzeigen und zu bearbeiten',

      // TTS
      'tts.title': 'Sprachsynthese',
      'tts.subtitle': 'Text in natürlich klingende Sprache umwandeln',
      'tts.voiceLabel': 'Stimme',
      'tts.loadingVoices': 'Stimmen werden geladen...',
      'tts.voicesError': 'Stimmen konnten nicht geladen werden',
      'tts.textLabel': 'Zu synthetisierender Text',
      'tts.textPlaceholder': 'Text hier eingeben...',
      'tts.generateButton': 'Sprache Generieren',
      'tts.generating': 'Wird generiert...',
      'tts.errorGeneric': 'Audio konnte nicht generiert werden',
      'tts.error402': 'Unzureichendes Guthaben. Bitte laden Sie Ihre Wallet über das Abrechnungsmenü auf, um TTS zu nutzen.',
      'tts.sampleTexts': 'Beispieltexte',
      'tts.streamingMode': 'Streaming-Modus',
      'tts.selectVoiceFirst': 'Bitte wählen Sie eine Stimme aus, um das Streaming zu starten',
      'tts.connecting': 'Verbinde...',
      'tts.connected': 'Verbunden',
      'tts.streaming': 'Streaming...',
      'tts.disconnected': 'Getrennt',
      'tts.stopStreaming': 'Stoppen',
      'tts.streamingError': 'Streaming-Fehler',
      'tts.downloadAudio': 'Audio herunterladen',
      'tts.play': 'Abspielen',
      'tts.pause': 'Pause',
      'tts.performanceMetrics': 'Leistungsmetriken',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Gesamtzeit',
      'tts.characters': 'Zeichen',
      'tts.audioDuration': 'Audiodauer',
      'tts.estimatedCost': 'Gesch. Kosten',
      'tts.requestId': 'Anfrage-ID',
      voiceGallery: 'Stimmen-Galerie',
      voiceGallerySubtitle: 'Stimmen durchsuchen, vorschau und klonen',
      stockVoices: 'Standard-Stimmen',
      myClonedVoices: 'Meine Geklonten Stimmen',
      addNewVoice: 'Neue Stimme Hinzufügen',
      cloneVoice: 'Stimme Klonen',
      voiceName: 'Stimmenname',
      voiceDescription: 'Beschreibung',
      uploadWavFiles: 'WAV-Dateien Hochladen',
      dragDropWav: 'WAV-Dateien hier ablegen oder zum Durchsuchen klicken',
      cloning: 'Klonen...',
      cloneSuccess: 'Stimme erfolgreich geklont',
      deleteVoice: 'Stimme Löschen',
      confirmDeleteVoice: 'Sind Sie sicher, dass Sie diese Stimme löschen möchten? Dies kann nicht rückgängig gemacht werden.',
      voiceDeleted: 'Stimme gelöscht',
      noClonedVoices: 'Noch keine geklonten Stimmen. Erstellen Sie Ihre erste benutzerdefinierte Stimme!',
      searchVoices: 'Stimmen suchen...',
      filterByLanguage: 'Sprache',
      allLanguages: 'Alle Sprachen',
      allTypes: 'Alle',
      stock: 'Standard',
      cloned: 'Geklont',
      tryVoice: 'Ausprobieren',
      preview: 'Vorschau',
      ttsQuickStart: 'TTS Schnellstart',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Sprachsynthese-Übersicht',
      ttsOverviewDescription: 'Wandeln Sie Text mit unserer TTS API in natürlich klingende Sprache um.',
      ttsStep1: 'Holen Sie sich Ihren API-Schlüssel',
      ttsStep2: 'Verfügbare Stimmen Auflisten',
      ttsStep3: 'Sprache Synthetisieren',
      ttsSupportedFormats: 'Unterstützte Ausgabeformate: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'TTS REST API Referenz',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Streamen Sie Text-zu-Sprache-Audio in Echtzeit über WebSocket-Verbindungen.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Gesamtzeichen',
      ttsUsageNote: 'TTS-Nutzungsverfolgung wird bald verfügbar sein. Ihre TTS-Kosten erscheinen im Abrechnungstransaktionsverlauf.',
      ttsCost: 'TTS-Kosten',
      ttsThisMonth: 'Diesen Monat',
      avgTtfb: 'Durchschn. TTFB',
      sharedBalance: 'Gemeinsames Guthaben für ASR- und TTS-Dienste',
      pricing: 'Preise',
      asrPricing: 'ASR - Spracherkennung',
      ttsPricing: 'TTS - Sprachsynthese',
      perMinuteRate: '€0,01 pro Minute Audio',
      standardVoiceRate: '€0,01 pro 1.000 Zeichen (Standard)',
      clonedVoiceRate: '€0,03 pro 1.000 Zeichen (geklont)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Sprache aufnehmen, transkribieren, bearbeiten und neu synthetisieren',
      'tts.recordOrUpload': 'Audio aufnehmen oder hochladen',
      'tts.recordAudio': 'Aufnehmen',
      'tts.uploadFile': 'Datei hochladen',
      'tts.transcribing': 'Transkribierung...',
      'tts.transcriptionComplete': 'Transkription abgeschlossen',
      'tts.editTranscription': 'Transkription bearbeiten',
      'tts.proceedToTts': 'Weiter zu TTS',
      'tts.speakIt': 'Synthetisieren',
      'tts.originalRecording': 'Originalaufnahme',
      'tts.ttsOutput': 'TTS-Ausgabe',
      'tts.step1Record': 'Aufnehmen',
      'tts.step2Transcribe': 'Transkribieren',
      'tts.step3Edit': 'Bearbeiten',
      'tts.step4Speak': 'Synthetisieren',
      'tts.startOver': 'Von vorne beginnen',
      'tts.compareAudio': 'Audio vergleichen',
      'tts.or': 'oder'
    }
  },
  nl: {
    translation: {
      // App title
      appTitle: 'Voxist AI Ontwikkelaarsportaal',
      tagline: 'AI Spraakplatform - ASR & TTS',
      
      // Login form
      welcomeBack: 'Welkom terug',
      signInPrompt: 'Log in op uw ontwikkelaarsaccount',
      signInWithEmail: 'Inloggen met E-mail',
      signInWithGoogle: 'Inloggen met Google',
      signInWithMicrosoft: 'Inloggen met Microsoft',
      
      // User menu
      userMenu: 'Gebruikersmenu',
      billing: 'Facturering',
      usage: 'Gebruik',
      apiKeys: 'API-sleutels',
      signOut: 'Uitloggen',
      
      // Language selector
      selectLanguage: 'Taal Selecteren',
      
      // Common
      close: 'Sluiten',
      cancel: 'Annuleren',
      save: 'Opslaan',
      loading: 'Laden...',
      error: 'Fout',
      success: 'Succes',
      
      // API Keys
      apiKeyManagement: 'API Sleutel Beheer',
      createApiKey: 'API Sleutel Maken',
      apiKeyName: 'API Sleutel Naam',
      apiKeyDescription: 'Beschrijving',
      generateKey: 'Sleutel Genereren',
      copyKey: 'Sleutel Kopiëren',
      keyCreated: 'API Sleutel Aangemaakt',
      keyRotated: 'API Sleutel Geroteerd',
      rotateKey: 'Sleutel Roteren',
      deleteKey: 'Sleutel Verwijderen',
      confirmDelete: 'Weet u zeker dat u deze API sleutel wilt verwijderen?',
      keyDeleted: 'API Sleutel Verwijderd',
      showKey: 'Sleutel Tonen',
      hideKey: 'Sleutel Verbergen',
      lastUsed: 'Laatst Gebruikt',
      createdAt: 'Aangemaakt',
      neverUsed: 'Nooit Gebruikt',
      active: 'Actief',
      inactive: 'Inactief',
      
      // Billing & Payments  
      billingPayments: 'Facturering & Betalingen',
      currentBalance: 'Huidig Saldo',
      addFunds: 'Geld Toevoegen',
      amount: 'Bedrag',
      topupWallet: 'Portemonnee Opladen',
      paymentSuccessful: 'Betaling Succesvol',
      paymentFailed: 'Betaling Mislukt',
      minimumAmount: 'Minimum: €0,50',
      maximumAmount: 'Maximum: €10.000,00',
      completePayment: 'Betaling Voltooien',
      paymentDetails: 'Betalingsgegevens',
      amountToPay: 'Te betalen bedrag',
      testCard: 'Testkaart: 4242 4242 4242 4242, elke toekomstige vervaldatum, elke CVC',
      paymentComplete: 'Betaling Voltooid',
      walletUpdated: 'Uw portemonnee wordt binnenkort bijgewerkt',
      
      // Transaction History
      transactionHistory: 'Transactiegeschiedenis',
      date: 'Datum',
      description: 'Beschrijving',
      type: 'Type',
      invoice: 'Factuur',
      payment: 'Betaling',
      noTransactions: 'Geen transacties gevonden',
      downloadInvoice: 'Factuur Downloaden',
      
      // Usage & Statistics
      usageStatistics: 'Gebruiksstatistieken',
      totalCalls: 'Totaal API Oproepen',
      thisMonth: 'Deze Maand',
      lastMonth: 'Vorige Maand',
      totalMinutes: 'Totaal Verwerkte Minuten',
      averageCallDuration: 'Gemiddelde Oproep Duur',
      mostUsedEngine: 'Meest Gebruikte Engine',
      callsPerDay: 'Oproepen per Dag',
      minutesPerDay: 'Minuten per Dag',
      usageBreakdown: 'Gebruiksverdeling',
      byEngine: 'Per Engine',
      byDate: 'Per Datum',
      
      // Missing keys from error logs
      usageAnalytics: 'Gebruiksanalyse',
      timeRange: 'Tijdsperiode',
      days: 'dagen',
      
      // Wallet & Credits
      walletBalance: 'Portemonnee Saldo',
      availableCredits: 'Beschikbare Credits',
      creditsUsed: 'Gebruikte Credits',
      creditTransaction: 'Credit Transactie',
      welcomeBonus: 'Welkomstbonus',
      stripePayment: 'Stripe Betaling',
      
      // Errors & Validation
      invalidAmount: 'Voer een geldig bedrag in',
      networkError: 'Netwerkfout opgetreden',
      paymentIntentFailed: 'Betalingsintentie aanmaken mislukt',
      billingDataError: 'Kan factureringsgegevens niet laden',
      deleteKeyError: 'Kan API sleutel niet verwijderen',
      createKeyError: 'Kan API sleutel niet aanmaken',
      rotateKeyError: 'Kan API sleutel niet roteren',
      
      // Developer Documentation
      developerDocs: 'Ontwikkelaarsdocumentatie',
      developerDocsSubtitle: "Integreer Voxist's krachtige spraakherkenning API in uw applicaties",
      websocketSamples: 'WebSocket Voorbeelden',
      apiReference: 'API Referentie',
      
      // Features
      realtimeProcessing: 'Realtime Verwerking',
      realtimeDescription: 'Stream audio en krijg directe transcriptieresultaten met onze WebSocket API',
      multiLanguageSupport: 'Meertalige Ondersteuning',
      multiLanguageDescription: 'Ondersteuning voor Nederlands, Engels, Frans, Duits, Spaans, Italiaans, Portugees, en meer',
      secureReliable: 'Veilig en Betrouwbaar',
      secureReliableDescription: 'Enterprise-grade beveiliging met API sleutel authenticatie en versleutelde verbindingen',
      
      // Quick Start
      quickStartGuide: 'Snelstart Gids',
      getStartedMinutes: 'Begin in Minuten',
      followStepsIntegrate: 'Volg deze stappen om Voxist ASR in uw applicatie te integreren',
      getApiKey: 'Krijg Uw API Sleutel',
      getApiKeyDescription: 'Genereer een API sleutel vanuit uw ontwikkelaarsportaal dashboard. Klik op het gebruikersmenu en selecteer "API Sleutels" om een nieuwe sleutel aan te maken.',
      uploadAudioFiles: 'Upload Audio Bestanden',
      uploadAudioDescription: 'Gebruik onze REST API om audio bestanden te uploaden voor transcriptie. Ondersteunde formaten: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Integreer Realtime',
      integrateRealtimeDescription: 'Voor realtime transcriptie, gebruik onze WebSocket API om audio te streamen en directe resultaten te ontvangen.',
      supportedLanguages: 'Ondersteunde Talen',
      
      // Language codes
      frenchLanguage: 'Frans',
      frenchMedical: 'Frans (Medisch)',
      englishLanguage: 'Engels',
      germanLanguage: 'Duits',
      spanishLanguage: 'Spaans',
      italianLanguage: 'Italiaans',
      portugueseLanguage: 'Portugees',
      dutchLanguage: 'Nederlands',
      
      // Tab Labels
      quickStart: 'Snelstart',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Authenticatie',
      
      // Examples descriptions
      curlExamplesTitle: 'cURL Voorbeelden',
      curlDescription: "Gebruik deze cURL commando's om de API direct vanuit uw terminal te testen.",
      nodeJsTitle: 'Node.js Integratie',
      nodeJsDescription: 'Volledig Node.js voorbeeld met axios en form-data bibliotheken.',
      pythonTitle: 'Python Integratie',
      pythonDescription: 'Python voorbeeld met de requests bibliotheek voor HTTP API aanroepen.',
      websocketTitle: 'WebSocket Realtime Transcriptie',
      websocketDescription: 'Voor realtime transcriptie, gebruik onze WebSocket API om audio data te streamen en directe transcriptieresultaten te ontvangen.',
      
      // WebSocket details
      completeWebsocketSamples: 'Volledige WebSocket Voorbeelden Beschikbaar',
      checkGithubRepo: 'Bekijk onze GitHub repository voor volledige WebSocket voorbeelden in meerdere talen',
      viewMoreExamples: 'Bekijk Meer Voorbeelden',
      websocketProtocol: 'WebSocket Protocol',
      connectionUrl: 'Verbindings URL',
      audioFormat: 'Audio Formaat',
      messageTypes: 'Berichttypen',
      audioFormatDetails: '16-bit PCM, 16kHz sample rate',
      messageTypesDetails: 'Verstuur binaire audio data, ontvang JSON tekst responses',
      
      // Authentication
      authenticationTitle: 'Authenticatie',
      apiKeyAuthentication: 'API Sleutel Authenticatie',
      apiKeyUsage: 'Gebruik uw API sleutel in de X-LVL-KEY header voor HTTP verzoeken:',
      jwtTokenAuthentication: 'JWT Token Authenticatie (WebSocket)',
      jwtDescription: 'Voor WebSocket verbindingen heeft u een JWT token nodig. Verkrijg er een via het OAuth endpoint:',
      securityBestPractices: 'Beveiligings Best Practices',
      securityTip1: 'Stel nooit uw API sleutels bloot in client-side code',
      securityTip2: 'Gebruik omgevingsvariabelen om credentials op te slaan',
      securityTip3: 'Roteer uw API sleutels regelmatig',
      securityTip4: 'Monitor uw API gebruik vanuit het ontwikkelaarsdashboard',
      
      // Footer
      needHelp: 'Hulp nodig? Neem contact op met ons support team of bekijk onze',
      githubSamples: 'GitHub voorbeelden',
      forMoreExamples: 'voor meer voorbeelden.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Beheerpaneel',
      billingManagement: 'Factureringsbeheer',
      walletCredits: 'Portemonnee Tegoed',
      customPricing: 'Aangepaste Prijzen',

      // Billing Tab
      userBillingSettings: 'Gebruikers Factureringsinstelling',
      billingSettingsDescription: 'Schakel automatische maandelijkse facturering voor gebruikers in of uit en stel hun factuurdag in.',
      user: 'Gebruiker',
      email: 'E-mail',
      stripeCustomer: 'Stripe Klant',
      autoInvoicing: 'Auto-Facturering',
      invoiceDay: 'Factuurdag',
      lastInvoice: 'Laatste Factuur',
      actions: 'Acties',
      edit: 'Bewerken',

      // Credits Tab
      addWalletCredits: 'Portemonnee Tegoed Toevoegen',
      creditsDescription: 'Voeg tegoed toe aan gebruikersportemonnees. Dit is alleen voor administratieve aanpassingen.',
      selectUser: 'Selecteer Gebruiker',
      reason: 'Reden',
      creditReasonPlaceholder: 'bijv. Promotioneel tegoed, terugbetaling, etc.',
      addCredit: 'Tegoed Toevoegen',
      adding: 'Toevoegen...',

      // Pricing Tab
      customUserPricing: 'Aangepaste Gebruikersprijzen',
      pricingDescription: 'Stel aangepaste transcriptieprijzen per gebruiker per engine in. Prijzen zijn in microeuros per seconde.',
      engine: 'Engine',
      defaultPrice: 'Standaardprijs',
      currentPrice: 'Huidige Prijs',
      newPrice: 'Nieuwe Prijs',
      status: 'Status',
      custom: 'Aangepast',
      default: 'Standaard',
      noChanges: 'Geen wijzigingen',
      refresh: 'Vernieuwen',
      noEnginesAvailable: 'Geen engines beschikbaar',
      selectUserToViewPricing: 'Selecteer een gebruiker om hun aangepaste prijzen te bekijken en bewerken',

      // TTS
      'tts.title': 'Tekst naar Spraak',
      'tts.subtitle': 'Tekst omzetten naar natuurlijk klinkende spraak',
      'tts.voiceLabel': 'Stem',
      'tts.loadingVoices': 'Stemmen laden...',
      'tts.voicesError': 'Stemmen laden mislukt',
      'tts.textLabel': 'Te synthetiseren tekst',
      'tts.textPlaceholder': 'Voer hier tekst in...',
      'tts.generateButton': 'Spraak Genereren',
      'tts.generating': 'Genereren...',
      'tts.errorGeneric': 'Audio genereren mislukt',
      'tts.error402': 'Onvoldoende saldo. Laad uw portemonnee op via het factuurmenu om TTS te gebruiken.',
      'tts.sampleTexts': 'Voorbeeldteksten',
      'tts.streamingMode': 'Streamingmodus',
      'tts.selectVoiceFirst': 'Selecteer een stem om streaming te starten',
      'tts.connecting': 'Verbinding maken...',
      'tts.connected': 'Verbonden',
      'tts.streaming': 'Streamen...',
      'tts.disconnected': 'Verbroken',
      'tts.stopStreaming': 'Stoppen',
      'tts.streamingError': 'Streamingfout',
      'tts.downloadAudio': 'Audio downloaden',
      'tts.play': 'Afspelen',
      'tts.pause': 'Pauze',
      'tts.performanceMetrics': 'Prestatiegegevens',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Totale Tijd',
      'tts.characters': 'Tekens',
      'tts.audioDuration': 'Audioduur',
      'tts.estimatedCost': 'Gesch. Kosten',
      'tts.requestId': 'Verzoek-ID',
      voiceGallery: 'Stemgalerie',
      voiceGallerySubtitle: 'Bladeren, voorbeluisteren en stemmen klonen',
      stockVoices: 'Standaard Stemmen',
      myClonedVoices: 'Mijn Gekloonde Stemmen',
      addNewVoice: 'Nieuwe Stem Toevoegen',
      cloneVoice: 'Stem Klonen',
      voiceName: 'Stemnaam',
      voiceDescription: 'Beschrijving',
      uploadWavFiles: 'WAV-bestanden Uploaden',
      dragDropWav: 'Sleep WAV-bestanden hierheen of klik om te bladeren',
      cloning: 'Klonen...',
      cloneSuccess: 'Stem succesvol gekloond',
      deleteVoice: 'Stem Verwijderen',
      confirmDeleteVoice: 'Weet u zeker dat u deze stem wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
      voiceDeleted: 'Stem verwijderd',
      noClonedVoices: 'Nog geen gekloonde stemmen. Maak uw eerste aangepaste stem!',
      searchVoices: 'Stemmen zoeken...',
      filterByLanguage: 'Taal',
      allLanguages: 'Alle Talen',
      allTypes: 'Alle',
      stock: 'Standaard',
      cloned: 'Gekloond',
      tryVoice: 'Probeer het',
      preview: 'Voorbeeld',
      ttsQuickStart: 'TTS Snelstart',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Tekst-naar-Spraak Overzicht',
      ttsOverviewDescription: 'Converteer tekst naar natuurlijk klinkende spraak met onze TTS API.',
      ttsStep1: 'Uw API Sleutel Ophalen',
      ttsStep2: 'Beschikbare Stemmen Weergeven',
      ttsStep3: 'Spraak Synthetiseren',
      ttsSupportedFormats: 'Ondersteunde uitvoerformaten: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'TTS REST API Referentie',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Stream tekst-naar-spraak audio in realtime via WebSocket-verbindingen.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Totale Tekens',
      ttsUsageNote: 'TTS-gebruiksregistratie is binnenkort beschikbaar. Uw TTS-kosten verschijnen in de factuurgeschiedenis.',
      ttsCost: 'TTS-kosten',
      ttsThisMonth: 'Deze Maand',
      avgTtfb: 'Gem. TTFB',
      sharedBalance: 'Gedeeld saldo voor ASR- en TTS-diensten',
      pricing: 'Prijzen',
      asrPricing: 'ASR - Spraak naar Tekst',
      ttsPricing: 'TTS - Tekst naar Spraak',
      perMinuteRate: '€0,01 per minuut audio',
      standardVoiceRate: '€0,01 per 1.000 tekens (standaard)',
      clonedVoiceRate: '€0,03 per 1.000 tekens (gekloond)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Spraak opnemen, transcriberen, bewerken en opnieuw synthetiseren',
      'tts.recordOrUpload': 'Audio opnemen of uploaden',
      'tts.recordAudio': 'Opnemen',
      'tts.uploadFile': 'Bestand uploaden',
      'tts.transcribing': 'Transcriberen...',
      'tts.transcriptionComplete': 'Transcriptie voltooid',
      'tts.editTranscription': 'Transcriptie bewerken',
      'tts.proceedToTts': 'Doorgaan naar TTS',
      'tts.speakIt': 'Synthetiseren',
      'tts.originalRecording': 'Originele opname',
      'tts.ttsOutput': 'TTS-uitvoer',
      'tts.step1Record': 'Opnemen',
      'tts.step2Transcribe': 'Transcriberen',
      'tts.step3Edit': 'Bewerken',
      'tts.step4Speak': 'Synthetiseren',
      'tts.startOver': 'Opnieuw beginnen',
      'tts.compareAudio': 'Audio vergelijken',
      'tts.or': 'of'
    }
  },
  it: {
    translation: {
      // App title
      appTitle: 'Portale Sviluppatori Voxist AI',
      tagline: 'Piattaforma IA Vocale - ASR & TTS',
      
      // Login form
      welcomeBack: 'Bentornato',
      signInPrompt: 'Accedi al tuo account sviluppatore',
      signInWithEmail: 'Accedi con Email',
      signInWithGoogle: 'Accedi con Google',
      signInWithMicrosoft: 'Accedi con Microsoft',
      
      // User menu
      userMenu: 'Menu utente',
      billing: 'Fatturazione',
      usage: 'Utilizzo',
      apiKeys: 'Chiavi API',
      signOut: 'Disconnetti',
      
      // Language selector
      selectLanguage: 'Seleziona Lingua',
      
      // Common
      close: 'Chiudi',
      cancel: 'Annulla',
      save: 'Salva',
      loading: 'Caricamento...',
      error: 'Errore',
      success: 'Successo',
      
      // API Keys
      apiKeyManagement: 'Gestione Chiavi API',
      createApiKey: 'Crea Chiave API',
      apiKeyName: 'Nome Chiave API',
      apiKeyDescription: 'Descrizione',
      generateKey: 'Genera Chiave',
      copyKey: 'Copia Chiave',
      keyCreated: 'Chiave API Creata',
      keyRotated: 'Chiave API Ruotata',
      rotateKey: 'Ruota Chiave',
      deleteKey: 'Elimina Chiave',
      confirmDelete: 'Sei sicuro di voler eliminare questa chiave API?',
      keyDeleted: 'Chiave API Eliminata',
      showKey: 'Mostra Chiave',
      hideKey: 'Nascondi Chiave',
      lastUsed: 'Ultimo Utilizzo',
      createdAt: 'Creata',
      neverUsed: 'Mai Utilizzata',
      active: 'Attiva',
      inactive: 'Inattiva',
      
      // Billing & Payments  
      billingPayments: 'Fatturazione e Pagamenti',
      currentBalance: 'Saldo Corrente',
      addFunds: 'Aggiungi Fondi',
      amount: 'Importo',
      topupWallet: 'Ricarica Portafoglio',
      paymentSuccessful: 'Pagamento Riuscito',
      paymentFailed: 'Pagamento Fallito',
      minimumAmount: 'Minimo: €0,50',
      maximumAmount: 'Massimo: €10.000,00',
      completePayment: 'Completa Pagamento',
      paymentDetails: 'Dettagli Pagamento',
      amountToPay: 'Importo da pagare',
      testCard: 'Carta di test: 4242 4242 4242 4242, qualsiasi scadenza futura, qualsiasi CVC',
      paymentComplete: 'Pagamento Completato',
      walletUpdated: 'Il tuo portafoglio sarà aggiornato a breve',
      
      // Transaction History
      transactionHistory: 'Cronologia Transazioni',
      date: 'Data',
      description: 'Descrizione',
      type: 'Tipo',
      invoice: 'Fattura',
      payment: 'Pagamento',
      noTransactions: 'Nessuna transazione trovata',
      downloadInvoice: 'Scarica Fattura',
      
      // Usage & Statistics
      usageStatistics: 'Statistiche di Utilizzo',
      totalCalls: 'Totale Chiamate API',
      thisMonth: 'Questo Mese',
      lastMonth: 'Mese Scorso',
      totalMinutes: 'Totale Minuti Elaborati',
      averageCallDuration: 'Durata Media Chiamata',
      mostUsedEngine: 'Engine Più Utilizzato',
      callsPerDay: 'Chiamate al Giorno',
      minutesPerDay: 'Minuti al Giorno',
      usageBreakdown: 'Ripartizione Utilizzo',
      byEngine: 'Per Engine',
      byDate: 'Per Data',
      
      // Missing keys from error logs
      usageAnalytics: 'Analisi di utilizzo',
      timeRange: 'Intervallo di tempo',
      days: 'giorni',
      
      // Wallet & Credits
      walletBalance: 'Saldo Portafoglio',
      availableCredits: 'Crediti Disponibili',
      creditsUsed: 'Crediti Utilizzati',
      creditTransaction: 'Transazione Credito',
      welcomeBonus: 'Bonus di Benvenuto',
      stripePayment: 'Pagamento Stripe',
      
      // Errors & Validation
      invalidAmount: 'Inserisci un importo valido',
      networkError: 'Errore di rete verificato',
      paymentIntentFailed: 'Creazione intenzione pagamento fallita',
      billingDataError: 'Impossibile caricare dati di fatturazione',
      deleteKeyError: 'Impossibile eliminare chiave API',
      createKeyError: 'Impossibile creare chiave API',
      rotateKeyError: 'Impossibile ruotare chiave API',
      
      // Developer Documentation
      developerDocs: 'Documentazione Sviluppatori',
      developerDocsSubtitle: 'Integra la potente API di riconoscimento vocale di Voxist nelle tue applicazioni',
      websocketSamples: 'Esempi WebSocket',
      apiReference: 'Riferimento API',
      
      // Features
      realtimeProcessing: 'Elaborazione in Tempo Reale',
      realtimeDescription: 'Trasmetti audio e ottieni risultati di trascrizione istantanei con la nostra API WebSocket',
      multiLanguageSupport: 'Supporto Multi-lingua',
      multiLanguageDescription: 'Supporto per italiano, inglese, francese, tedesco, spagnolo, portoghese, olandese e altro',
      secureReliable: 'Sicuro e Affidabile',
      secureReliableDescription: 'Sicurezza di livello enterprise con autenticazione tramite chiave API e connessioni crittografate',
      
      // Quick Start
      quickStartGuide: 'Guida Rapida',
      getStartedMinutes: 'Inizia in Minuti',
      followStepsIntegrate: 'Segui questi passaggi per integrare Voxist ASR nella tua applicazione',
      getApiKey: 'Ottieni la Tua Chiave API',
      getApiKeyDescription: 'Genera una chiave API dal tuo dashboard del portale sviluppatori. Clicca sul menu utente e seleziona "Chiavi API" per creare una nuova chiave.',
      uploadAudioFiles: 'Carica File Audio',
      uploadAudioDescription: 'Usa la nostra API REST per caricare file audio per la trascrizione. Formati supportati: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Integra Tempo Reale',
      integrateRealtimeDescription: 'Per la trascrizione in tempo reale, usa la nostra API WebSocket per trasmettere audio e ricevere risultati istantanei.',
      supportedLanguages: 'Lingue Supportate',
      
      // Language codes
      frenchLanguage: 'Francese',
      frenchMedical: 'Francese (Medico)',
      englishLanguage: 'Inglese',
      germanLanguage: 'Tedesco',
      spanishLanguage: 'Spagnolo',
      italianLanguage: 'Italiano',
      portugueseLanguage: 'Portoghese',
      dutchLanguage: 'Olandese',
      
      // Tab Labels
      quickStart: 'Avvio Rapido',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Autenticazione',
      
      // Examples descriptions
      curlExamplesTitle: 'Esempi cURL',
      curlDescription: "Usa questi comandi cURL per testare l'API direttamente dal tuo terminale.",
      nodeJsTitle: 'Integrazione Node.js',
      nodeJsDescription: 'Esempio completo Node.js usando le librerie axios e form-data.',
      pythonTitle: 'Integrazione Python',
      pythonDescription: 'Esempio Python usando la libreria requests per chiamate API HTTP.',
      websocketTitle: 'Trascrizione WebSocket in Tempo Reale',
      websocketDescription: 'Per la trascrizione in tempo reale, usa la nostra API WebSocket per trasmettere dati audio e ricevere risultati di trascrizione istantanei.',
      
      // WebSocket details
      completeWebsocketSamples: 'Esempi WebSocket Completi Disponibili',
      checkGithubRepo: 'Controlla il nostro repository GitHub per esempi WebSocket completi in più linguaggi',
      viewMoreExamples: 'Visualizza Altri Esempi',
      websocketProtocol: 'Protocollo WebSocket',
      connectionUrl: 'URL di Connessione',
      audioFormat: 'Formato Audio',
      messageTypes: 'Tipi di Messaggio',
      audioFormatDetails: 'PCM 16-bit, frequenza di campionamento 16kHz',
      messageTypesDetails: 'Invia dati audio binari, ricevi risposte di testo JSON',
      
      // Authentication
      authenticationTitle: 'Autenticazione',
      apiKeyAuthentication: 'Autenticazione Chiave API',
      apiKeyUsage: "Usa la tua chiave API nell'header X-LVL-KEY per le richieste HTTP:",
      jwtTokenAuthentication: 'Autenticazione Token JWT (WebSocket)',
      jwtDescription: "Per le connessioni WebSocket, hai bisogno di un token JWT. Ottienine uno usando l'endpoint OAuth:",
      securityBestPractices: 'Migliori Pratiche di Sicurezza',
      securityTip1: 'Mai esporre le tue chiavi API nel codice lato client',
      securityTip2: "Usa variabili d'ambiente per memorizzare le credenziali",
      securityTip3: 'Ruota regolarmente le tue chiavi API',
      securityTip4: "Monitora l'uso della tua API dal dashboard sviluppatori",
      
      // Footer
      needHelp: 'Hai bisogno di aiuto? Contatta il nostro team di supporto o consulta i nostri',
      githubSamples: 'esempi GitHub',
      forMoreExamples: 'per altri esempi.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Pannello di Amministrazione',
      billingManagement: 'Gestione Fatturazione',
      walletCredits: 'Crediti Portafoglio',
      customPricing: 'Prezzi Personalizzati',

      // Billing Tab
      userBillingSettings: 'Impostazioni Fatturazione Utenti',
      billingSettingsDescription: 'Abilita o disabilita la fatturazione automatica mensile per gli utenti e imposta il loro giorno di fatturazione.',
      user: 'Utente',
      email: 'Email',
      stripeCustomer: 'Cliente Stripe',
      autoInvoicing: 'Fatturazione Auto',
      invoiceDay: 'Giorno Fattura',
      lastInvoice: 'Ultima Fattura',
      actions: 'Azioni',
      edit: 'Modifica',

      // Credits Tab
      addWalletCredits: 'Aggiungi Crediti Portafoglio',
      creditsDescription: 'Aggiungi crediti ai portafogli degli utenti. Questo è solo per aggiustamenti amministrativi.',
      selectUser: 'Seleziona Utente',
      reason: 'Motivo',
      creditReasonPlaceholder: 'es. Credito promozionale, rimborso, ecc.',
      addCredit: 'Aggiungi Credito',
      adding: 'Aggiungendo...',

      // Pricing Tab
      customUserPricing: 'Prezzi Utente Personalizzati',
      pricingDescription: 'Imposta prezzi di trascrizione personalizzati per utente per engine. I prezzi sono in microeuro al secondo.',
      engine: 'Engine',
      defaultPrice: 'Prezzo Predefinito',
      currentPrice: 'Prezzo Corrente',
      newPrice: 'Nuovo Prezzo',
      status: 'Stato',
      custom: 'Personalizzato',
      default: 'Predefinito',
      noChanges: 'Nessuna modifica',
      refresh: 'Aggiorna',
      noEnginesAvailable: 'Nessun engine disponibile',
      selectUserToViewPricing: 'Seleziona un utente per visualizzare e modificare i suoi prezzi personalizzati',

      // TTS
      'tts.title': 'Sintesi Vocale',
      'tts.subtitle': 'Converti il testo in parlato naturale',
      'tts.voiceLabel': 'Voce',
      'tts.loadingVoices': 'Caricamento voci...',
      'tts.voicesError': 'Impossibile caricare le voci',
      'tts.textLabel': 'Testo da sintetizzare',
      'tts.textPlaceholder': 'Inserisci il testo qui...',
      'tts.generateButton': 'Genera Parlato',
      'tts.generating': 'Generazione...',
      'tts.errorGeneric': "Impossibile generare l'audio",
      'tts.error402': 'Saldo insufficiente. Si prega di ricaricare il portafoglio tramite il menu di fatturazione per utilizzare TTS.',
      'tts.sampleTexts': 'Testi di Esempio',
      'tts.streamingMode': 'Modalità streaming',
      'tts.selectVoiceFirst': 'Seleziona una voce per iniziare lo streaming',
      'tts.connecting': 'Connessione...',
      'tts.connected': 'Connesso',
      'tts.streaming': 'Streaming...',
      'tts.disconnected': 'Disconnesso',
      'tts.stopStreaming': 'Ferma',
      'tts.streamingError': 'Errore di streaming',
      'tts.downloadAudio': 'Scarica audio',
      'tts.play': 'Riproduci',
      'tts.pause': 'Pausa',
      'tts.performanceMetrics': 'Metriche di Prestazione',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Tempo Totale',
      'tts.characters': 'Caratteri',
      'tts.audioDuration': 'Durata Audio',
      'tts.estimatedCost': 'Costo Stim.',
      'tts.requestId': 'ID Richiesta',
      voiceGallery: 'Galleria Voci',
      voiceGallerySubtitle: 'Sfoglia, anteprima e clona voci',
      stockVoices: 'Voci Standard',
      myClonedVoices: 'Le Mie Voci Clonate',
      addNewVoice: 'Aggiungi Nuova Voce',
      cloneVoice: 'Clona Voce',
      voiceName: 'Nome Voce',
      voiceDescription: 'Descrizione',
      uploadWavFiles: 'Carica File WAV',
      dragDropWav: 'Trascina i file WAV qui o clicca per sfogliare',
      cloning: 'Clonazione...',
      cloneSuccess: 'Voce clonata con successo',
      deleteVoice: 'Elimina Voce',
      confirmDeleteVoice: 'Sei sicuro di voler eliminare questa voce? Questa azione non può essere annullata.',
      voiceDeleted: 'Voce eliminata',
      noClonedVoices: 'Nessuna voce clonata ancora. Crea la tua prima voce personalizzata!',
      searchVoices: 'Cerca voci...',
      filterByLanguage: 'Lingua',
      allLanguages: 'Tutte le Lingue',
      allTypes: 'Tutti',
      stock: 'Standard',
      cloned: 'Clonata',
      tryVoice: 'Prova',
      preview: 'Anteprima',
      ttsQuickStart: 'TTS Avvio Rapido',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Panoramica Sintesi Vocale',
      ttsOverviewDescription: 'Converti il testo in parlato naturale usando la nostra API TTS.',
      ttsStep1: 'Ottieni la Tua Chiave API',
      ttsStep2: 'Elenca le Voci Disponibili',
      ttsStep3: 'Sintetizza il Parlato',
      ttsSupportedFormats: 'Formati di output supportati: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'Riferimento TTS REST API',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Trasmetti audio di sintesi vocale in tempo reale tramite connessioni WebSocket.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Totale Caratteri',
      ttsUsageNote: "Il monitoraggio dell'utilizzo TTS sarà disponibile a breve. I costi TTS appaiono nella cronologia delle transazioni di fatturazione.",
      ttsCost: 'Costo TTS',
      ttsThisMonth: 'Questo Mese',
      avgTtfb: 'TTFB Med.',
      sharedBalance: 'Saldo condiviso per i servizi ASR e TTS',
      pricing: 'Prezzi',
      asrPricing: 'ASR - Riconoscimento Vocale',
      ttsPricing: 'TTS - Sintesi Vocale',
      perMinuteRate: '€0,01 al minuto di audio',
      standardVoiceRate: '€0,01 per 1.000 caratteri (standard)',
      clonedVoiceRate: '€0,03 per 1.000 caratteri (clonata)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Registra la voce, trascrivi, modifica e risintettizza',
      'tts.recordOrUpload': 'Registra o carica audio',
      'tts.recordAudio': 'Registra',
      'tts.uploadFile': 'Carica file',
      'tts.transcribing': 'Trascrizione in corso...',
      'tts.transcriptionComplete': 'Trascrizione completata',
      'tts.editTranscription': 'Modifica trascrizione',
      'tts.proceedToTts': 'Vai al TTS',
      'tts.speakIt': 'Sintetizza',
      'tts.originalRecording': 'Registrazione originale',
      'tts.ttsOutput': 'Output TTS',
      'tts.step1Record': 'Registra',
      'tts.step2Transcribe': 'Trascrivi',
      'tts.step3Edit': 'Modifica',
      'tts.step4Speak': 'Sintetizza',
      'tts.startOver': 'Ricomincia',
      'tts.compareAudio': 'Confronta audio',
      'tts.or': 'oppure'
    }
  },
  es: {
    translation: {
      // App title
      appTitle: 'Portal de Desarrollador Voxist AI',
      tagline: 'Plataforma IA de Voz - ASR & TTS',
      
      // Login form
      welcomeBack: 'Bienvenido de nuevo',
      signInPrompt: 'Inicia sesión en tu cuenta de desarrollador',
      signInWithEmail: 'Iniciar sesión con Email',
      signInWithGoogle: 'Iniciar sesión con Google',
      signInWithMicrosoft: 'Iniciar sesión con Microsoft',
      
      // User menu
      userMenu: 'Menú de usuario',
      billing: 'Facturación',
      usage: 'Uso',
      apiKeys: 'Claves API',
      signOut: 'Cerrar Sesión',
      
      // Language selector
      selectLanguage: 'Seleccionar Idioma',
      
      // Common
      close: 'Cerrar',
      cancel: 'Cancelar',
      save: 'Guardar',
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      
      // API Keys
      apiKeyManagement: 'Gestión de Claves API',
      createApiKey: 'Crear Clave API',
      apiKeyName: 'Nombre de Clave API',
      apiKeyDescription: 'Descripción',
      generateKey: 'Generar Clave',
      copyKey: 'Copiar Clave',
      keyCreated: 'Clave API Creada',
      keyRotated: 'Clave API Rotada',
      rotateKey: 'Rotar Clave',
      deleteKey: 'Eliminar Clave',
      confirmDelete: '¿Está seguro de que desea eliminar esta clave API?',
      keyDeleted: 'Clave API Eliminada',
      showKey: 'Mostrar Clave',
      hideKey: 'Ocultar Clave',
      lastUsed: 'Último Uso',
      createdAt: 'Creada',
      neverUsed: 'Nunca Usada',
      active: 'Activa',
      inactive: 'Inactiva',
      
      // Billing & Payments  
      billingPayments: 'Facturación y Pagos',
      currentBalance: 'Saldo Actual',
      addFunds: 'Agregar Fondos',
      amount: 'Cantidad',
      topupWallet: 'Recargar Billetera',
      paymentSuccessful: 'Pago Exitoso',
      paymentFailed: 'Pago Fallido',
      minimumAmount: 'Mínimo: €0,50',
      maximumAmount: 'Máximo: €10.000,00',
      completePayment: 'Completar Pago',
      paymentDetails: 'Detalles del Pago',
      amountToPay: 'Cantidad a pagar',
      testCard: 'Tarjeta de prueba: 4242 4242 4242 4242, cualquier fecha futura, cualquier CVC',
      paymentComplete: 'Pago Completado',
      walletUpdated: 'Su billetera se actualizará en breve',
      
      // Transaction History
      transactionHistory: 'Historial de Transacciones',
      date: 'Fecha',
      description: 'Descripción',
      type: 'Tipo',
      invoice: 'Factura',
      payment: 'Pago',
      noTransactions: 'No se encontraron transacciones',
      downloadInvoice: 'Descargar Factura',
      
      // Usage & Statistics
      usageStatistics: 'Estadísticas de Uso',
      totalCalls: 'Total de Llamadas API',
      thisMonth: 'Este Mes',
      lastMonth: 'Mes Pasado',
      totalMinutes: 'Total de Minutos Procesados',
      averageCallDuration: 'Duración Promedio de Llamada',
      mostUsedEngine: 'Motor Más Utilizado',
      callsPerDay: 'Llamadas por Día',
      minutesPerDay: 'Minutos por Día',
      usageBreakdown: 'Desglose de Uso',
      byEngine: 'Por Motor',
      byDate: 'Por Fecha',
      
      // Missing keys from error logs
      usageAnalytics: 'Análisis de uso',
      timeRange: 'Rango de tiempo',
      days: 'días',
      
      // Wallet & Credits
      walletBalance: 'Saldo de Billetera',
      availableCredits: 'Créditos Disponibles',
      creditsUsed: 'Créditos Utilizados',
      creditTransaction: 'Transacción de Crédito',
      welcomeBonus: 'Bono de Bienvenida',
      stripePayment: 'Pago Stripe',
      
      // Errors & Validation
      invalidAmount: 'Por favor ingrese una cantidad válida',
      networkError: 'Error de red ocurrido',
      paymentIntentFailed: 'Falló la creación de intención de pago',
      billingDataError: 'No se pudieron cargar los datos de facturación',
      deleteKeyError: 'No se pudo eliminar la clave API',
      createKeyError: 'No se pudo crear la clave API',
      rotateKeyError: 'No se pudo rotar la clave API',
      
      // Developer Documentation
      developerDocs: 'Documentación para Desarrolladores',
      developerDocsSubtitle: 'Integre la potente API de reconocimiento de voz de Voxist en sus aplicaciones',
      websocketSamples: 'Ejemplos WebSocket',
      apiReference: 'Referencia API',
      
      // Features
      realtimeProcessing: 'Procesamiento en Tiempo Real',
      realtimeDescription: 'Transmita audio y obtenga resultados de transcripción instantáneos con nuestra API WebSocket',
      multiLanguageSupport: 'Soporte Multi-idioma',
      multiLanguageDescription: 'Soporte para español, inglés, francés, alemán, italiano, portugués, holandés y más',
      secureReliable: 'Seguro y Confiable',
      secureReliableDescription: 'Seguridad de nivel empresarial con autenticación por clave API y conexiones cifradas',
      
      // Quick Start
      quickStartGuide: 'Guía de Inicio Rápido',
      getStartedMinutes: 'Comience en Minutos',
      followStepsIntegrate: 'Siga estos pasos para integrar Voxist ASR en su aplicación',
      getApiKey: 'Obtenga su Clave API',
      getApiKeyDescription: 'Genere una clave API desde su panel de portal de desarrolladores. Haga clic en el menú de usuario y seleccione "Claves API" para crear una nueva clave.',
      uploadAudioFiles: 'Subir Archivos de Audio',
      uploadAudioDescription: 'Use nuestra API REST para subir archivos de audio para transcripción. Formatos soportados: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Integrar Tiempo Real',
      integrateRealtimeDescription: 'Para transcripción en tiempo real, use nuestra API WebSocket para transmitir audio y recibir resultados instantáneos.',
      supportedLanguages: 'Idiomas Soportados',
      
      // Language codes
      frenchLanguage: 'Francés',
      frenchMedical: 'Francés (Médico)',
      englishLanguage: 'Inglés',
      germanLanguage: 'Alemán',
      spanishLanguage: 'Español',
      italianLanguage: 'Italiano',
      portugueseLanguage: 'Portugués',
      dutchLanguage: 'Holandés',
      
      // Tab Labels
      quickStart: 'Inicio Rápido',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Autenticación',
      
      // Examples descriptions
      curlExamplesTitle: 'Ejemplos cURL',
      curlDescription: 'Use estos comandos cURL para probar la API directamente desde su terminal.',
      nodeJsTitle: 'Integración Node.js',
      nodeJsDescription: 'Ejemplo completo de Node.js usando las librerías axios y form-data.',
      pythonTitle: 'Integración Python',
      pythonDescription: 'Ejemplo de Python usando la librería requests para llamadas a la API HTTP.',
      websocketTitle: 'Transcripción WebSocket en Tiempo Real',
      websocketDescription: 'Para transcripción en tiempo real, use nuestra API WebSocket para transmitir datos de audio y recibir resultados de transcripción instantáneos.',
      
      // WebSocket details
      completeWebsocketSamples: 'Ejemplos WebSocket Completos Disponibles',
      checkGithubRepo: 'Consulte nuestro repositorio GitHub para ejemplos WebSocket completos en múltiples lenguajes',
      viewMoreExamples: 'Ver Más Ejemplos',
      websocketProtocol: 'Protocolo WebSocket',
      connectionUrl: 'URL de Conexión',
      audioFormat: 'Formato de Audio',
      messageTypes: 'Tipos de Mensaje',
      audioFormatDetails: 'PCM 16-bit, frecuencia de muestreo 16kHz',
      messageTypesDetails: 'Enviar datos de audio binarios, recibir respuestas de texto JSON',
      
      // Authentication
      authenticationTitle: 'Autenticación',
      apiKeyAuthentication: 'Autenticación por Clave API',
      apiKeyUsage: 'Use su clave API en el header X-LVL-KEY para solicitudes HTTP:',
      jwtTokenAuthentication: 'Autenticación por Token JWT (WebSocket)',
      jwtDescription: 'Para conexiones WebSocket, necesita un token JWT. Obtenga uno usando el endpoint OAuth:',
      securityBestPractices: 'Mejores Prácticas de Seguridad',
      securityTip1: 'Nunca exponga sus claves API en código del lado del cliente',
      securityTip2: 'Use variables de entorno para almacenar credenciales',
      securityTip3: 'Rote regularmente sus claves API',
      securityTip4: 'Monitoree el uso de su API desde el panel de desarrolladores',
      
      // Footer
      needHelp: '¿Necesita ayuda? Contacte a nuestro equipo de soporte o consulte nuestros',
      githubSamples: 'ejemplos de GitHub',
      forMoreExamples: 'para más ejemplos.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Panel de Administración',
      billingManagement: 'Gestión de Facturación',
      walletCredits: 'Créditos de Billetera',
      customPricing: 'Precios Personalizados',

      // Billing Tab
      userBillingSettings: 'Configuración de Facturación de Usuarios',
      billingSettingsDescription: 'Habilite o deshabilite la facturación automática mensual para usuarios y establezca su día de factura.',
      user: 'Usuario',
      email: 'Correo Electrónico',
      stripeCustomer: 'Cliente Stripe',
      autoInvoicing: 'Facturación Auto',
      invoiceDay: 'Día de Factura',
      lastInvoice: 'Última Factura',
      actions: 'Acciones',
      edit: 'Editar',

      // Credits Tab
      addWalletCredits: 'Agregar Créditos a Billetera',
      creditsDescription: 'Agregue créditos a las billeteras de usuarios. Esto es solo para ajustes administrativos.',
      selectUser: 'Seleccionar Usuario',
      reason: 'Razón',
      creditReasonPlaceholder: 'ej. Crédito promocional, reembolso, etc.',
      addCredit: 'Agregar Crédito',
      adding: 'Agregando...',

      // Pricing Tab
      customUserPricing: 'Precios de Usuario Personalizados',
      pricingDescription: 'Establezca precios de transcripción personalizados por usuario por motor. Los precios están en microeuros por segundo.',
      engine: 'Motor',
      defaultPrice: 'Precio Predeterminado',
      currentPrice: 'Precio Actual',
      newPrice: 'Nuevo Precio',
      status: 'Estado',
      custom: 'Personalizado',
      default: 'Predeterminado',
      noChanges: 'Sin cambios',
      refresh: 'Actualizar',
      noEnginesAvailable: 'No hay motores disponibles',
      selectUserToViewPricing: 'Seleccione un usuario para ver y editar sus precios personalizados',

      // TTS
      'tts.title': 'Texto a Voz',
      'tts.subtitle': 'Convierte texto en voz de sonido natural',
      'tts.voiceLabel': 'Voz',
      'tts.loadingVoices': 'Cargando voces...',
      'tts.voicesError': 'Error al cargar voces',
      'tts.textLabel': 'Texto a sintetizar',
      'tts.textPlaceholder': 'Ingrese texto aquí...',
      'tts.generateButton': 'Generar Voz',
      'tts.generating': 'Generando...',
      'tts.errorGeneric': 'Error al generar audio',
      'tts.error402': 'Saldo insuficiente. Por favor recargue su billetera desde el menú de facturación para usar TTS.',
      'tts.sampleTexts': 'Textos de Ejemplo',
      'tts.streamingMode': 'Modo streaming',
      'tts.selectVoiceFirst': 'Por favor seleccione una voz para iniciar el streaming',
      'tts.connecting': 'Conectando...',
      'tts.connected': 'Conectado',
      'tts.streaming': 'Transmitiendo...',
      'tts.disconnected': 'Desconectado',
      'tts.stopStreaming': 'Detener',
      'tts.streamingError': 'Error de streaming',
      'tts.downloadAudio': 'Descargar audio',
      'tts.play': 'Reproducir',
      'tts.pause': 'Pausar',
      'tts.performanceMetrics': 'Métricas de Rendimiento',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Tiempo Total',
      'tts.characters': 'Caracteres',
      'tts.audioDuration': 'Duración del Audio',
      'tts.estimatedCost': 'Costo Est.',
      'tts.requestId': 'ID de Solicitud',
      voiceGallery: 'Galería de Voces',
      voiceGallerySubtitle: 'Explorar, previsualizar y clonar voces',
      stockVoices: 'Voces Estándar',
      myClonedVoices: 'Mis Voces Clonadas',
      addNewVoice: 'Agregar Nueva Voz',
      cloneVoice: 'Clonar Voz',
      voiceName: 'Nombre de Voz',
      voiceDescription: 'Descripción',
      uploadWavFiles: 'Subir Archivos WAV',
      dragDropWav: 'Arrastre y suelte archivos WAV aquí o haga clic para explorar',
      cloning: 'Clonando...',
      cloneSuccess: 'Voz clonada exitosamente',
      deleteVoice: 'Eliminar Voz',
      confirmDeleteVoice: '¿Está seguro de que desea eliminar esta voz? Esta acción no se puede deshacer.',
      voiceDeleted: 'Voz eliminada',
      noClonedVoices: 'Aún no hay voces clonadas. ¡Cree su primera voz personalizada!',
      searchVoices: 'Buscar voces...',
      filterByLanguage: 'Idioma',
      allLanguages: 'Todos los Idiomas',
      allTypes: 'Todos',
      stock: 'Estándar',
      cloned: 'Clonada',
      tryVoice: 'Probar',
      preview: 'Vista Previa',
      ttsQuickStart: 'Inicio Rápido TTS',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Descripción General de Texto a Voz',
      ttsOverviewDescription: 'Convierte texto en voz de sonido natural usando nuestra API TTS.',
      ttsStep1: 'Obtén Tu Clave API',
      ttsStep2: 'Lista las Voces Disponibles',
      ttsStep3: 'Sintetiza la Voz',
      ttsSupportedFormats: 'Formatos de salida soportados: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'Referencia TTS REST API',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Transmite audio de texto a voz en tiempo real usando conexiones WebSocket.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Total de Caracteres',
      ttsUsageNote: 'El seguimiento del uso de TTS estará disponible pronto. Sus costos de TTS aparecen en el historial de transacciones de facturación.',
      ttsCost: 'Costo TTS',
      ttsThisMonth: 'Este Mes',
      avgTtfb: 'TTFB Prom.',
      sharedBalance: 'Saldo compartido para servicios ASR y TTS',
      pricing: 'Precios',
      asrPricing: 'ASR - Voz a Texto',
      ttsPricing: 'TTS - Texto a Voz',
      perMinuteRate: '€0,01 por minuto de audio',
      standardVoiceRate: '€0,01 por 1.000 caracteres (estándar)',
      clonedVoiceRate: '€0,03 por 1.000 caracteres (clonada)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Graba voz, transcribe, edita y re-sintetiza',
      'tts.recordOrUpload': 'Grabar o subir audio',
      'tts.recordAudio': 'Grabar',
      'tts.uploadFile': 'Subir archivo',
      'tts.transcribing': 'Transcribiendo...',
      'tts.transcriptionComplete': 'Transcripción completa',
      'tts.editTranscription': 'Editar transcripción',
      'tts.proceedToTts': 'Ir a TTS',
      'tts.speakIt': 'Sintetizar',
      'tts.originalRecording': 'Grabación original',
      'tts.ttsOutput': 'Salida TTS',
      'tts.step1Record': 'Grabar',
      'tts.step2Transcribe': 'Transcribir',
      'tts.step3Edit': 'Editar',
      'tts.step4Speak': 'Sintetizar',
      'tts.startOver': 'Empezar de nuevo',
      'tts.compareAudio': 'Comparar audio',
      'tts.or': 'o'
    }
  },
  sv: {
    translation: {
      // App title
      appTitle: 'Voxist AI Utvecklarportal',
      tagline: 'AI-röstplattform - ASR & TTS',
      
      // Login form
      welcomeBack: 'Välkommen tillbaka',
      signInPrompt: 'Logga in på ditt utvecklarkonto',
      signInWithEmail: 'Logga in med E-post',
      signInWithGoogle: 'Logga in med Google',
      signInWithMicrosoft: 'Logga in med Microsoft',
      
      // User menu
      userMenu: 'Användarmeny',
      billing: 'Fakturering',
      usage: 'Användning',
      apiKeys: 'API-nycklar',
      signOut: 'Logga ut',
      
      // Language selector
      selectLanguage: 'Välj språk',
      
      // Common
      close: 'Stäng',
      cancel: 'Avbryt',
      save: 'Spara',
      loading: 'Laddar...',
      error: 'Fel',
      success: 'Framgång',
      
      // API Keys
      apiKeyManagement: 'API-nyckel Hantering',
      createApiKey: 'Skapa API-nyckel',
      apiKeyName: 'API-nyckel Namn',
      apiKeyDescription: 'Beskrivning',
      generateKey: 'Generera Nyckel',
      copyKey: 'Kopiera Nyckel',
      keyCreated: 'API-nyckel Skapad',
      keyRotated: 'API-nyckel Roterad',
      rotateKey: 'Rotera Nyckel',
      deleteKey: 'Radera Nyckel',
      confirmDelete: 'Är du säker på att du vill radera denna API-nyckel?',
      keyDeleted: 'API-nyckel Raderad',
      showKey: 'Visa Nyckel',
      hideKey: 'Dölj Nyckel',
      lastUsed: 'Senast Använd',
      createdAt: 'Skapad',
      neverUsed: 'Aldrig Använd',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      
      // Billing & Payments  
      billingPayments: 'Fakturering & Betalningar',
      currentBalance: 'Nuvarande Saldo',
      addFunds: 'Lägg till Medel',
      amount: 'Belopp',
      topupWallet: 'Ladda upp Plånbok',
      paymentSuccessful: 'Betalning Lyckades',
      paymentFailed: 'Betalning Misslyckades',
      minimumAmount: 'Minimum: €0,50',
      maximumAmount: 'Maximum: €10.000,00',
      completePayment: 'Slutför Betalning',
      paymentDetails: 'Betalningsdetaljer',
      amountToPay: 'Belopp att betala',
      testCard: 'Testkort: 4242 4242 4242 4242, vilket framtida utgångsdatum som helst, vilket CVC som helst',
      paymentComplete: 'Betalning Klar',
      walletUpdated: 'Din plånbok kommer att uppdateras snart',
      
      // Transaction History
      transactionHistory: 'Transaktionshistorik',
      date: 'Datum',
      description: 'Beskrivning',
      type: 'Typ',
      invoice: 'Faktura',
      payment: 'Betalning',
      noTransactions: 'Inga transaktioner hittades',
      downloadInvoice: 'Ladda ner Faktura',
      
      // Usage & Statistics
      usageStatistics: 'Användningsstatistik',
      totalCalls: 'Totala API-anrop',
      thisMonth: 'Denna Månad',
      lastMonth: 'Förra Månaden',
      totalMinutes: 'Totala Bearbetade Minuter',
      averageCallDuration: 'Genomsnittlig Samtalslängd',
      mostUsedEngine: 'Mest Använda Motor',
      callsPerDay: 'Anrop per Dag',
      minutesPerDay: 'Minuter per Dag',
      usageBreakdown: 'Användningsfördelning',
      byEngine: 'Per Motor',
      byDate: 'Per Datum',
      
      // Missing keys from error logs
      usageAnalytics: 'Användningsanalys',
      timeRange: 'Tidsperiod',
      days: 'dagar',
      
      // Wallet & Credits
      walletBalance: 'Plånbokssaldo',
      availableCredits: 'Tillgängliga Krediter',
      creditsUsed: 'Använda Krediter',
      creditTransaction: 'Kredit-transaktion',
      welcomeBonus: 'Välkomstbonus',
      stripePayment: 'Stripe Betalning',
      
      // Errors & Validation
      invalidAmount: 'Vänligen ange ett giltigt belopp',
      networkError: 'Nätverksfel inträffade',
      paymentIntentFailed: 'Skapande av betalningsavsikt misslyckades',
      billingDataError: 'Kunde inte ladda faktureringsdata',
      deleteKeyError: 'Kunde inte radera API-nyckel',
      createKeyError: 'Kunde inte skapa API-nyckel',
      rotateKeyError: 'Kunde inte rotera API-nyckel',
      
      // Developer Documentation
      developerDocs: 'Utvecklardokumentation',
      developerDocsSubtitle: 'Integrera Voxists kraftfulla taligenkännings-API i dina applikationer',
      websocketSamples: 'WebSocket Exempel',
      apiReference: 'API Referens',
      
      // Features
      realtimeProcessing: 'Realtidsbearbetning',
      realtimeDescription: 'Strömma audio och få omedelbar transkriptionsresultat med vår WebSocket API',
      multiLanguageSupport: 'Flerspråkigt Stöd',
      multiLanguageDescription: 'Stöd för svenska, engelska, franska, tyska, spanska, italienska, portugisiska, holländska och mer',
      secureReliable: 'Säker och Pålitlig',
      secureReliableDescription: 'Företagsnivå säkerhet med API-nyckel autentisering och krypterade anslutningar',
      
      // Quick Start
      quickStartGuide: 'Snabbstartsguide',
      getStartedMinutes: 'Kom Igång på Minuter',
      followStepsIntegrate: 'Följ dessa steg för att integrera Voxist ASR i din applikation',
      getApiKey: 'Få din API-nyckel',
      getApiKeyDescription: 'Generera en API-nyckel från din utvecklarportal dashboard. Klicka på användarmenyn och välj "API-nycklar" för att skapa en ny nyckel.',
      uploadAudioFiles: 'Ladda upp Audiofiler',
      uploadAudioDescription: 'Använd vår REST API för att ladda upp audiofiler för transkription. Stödda format: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Integrera Realtid',
      integrateRealtimeDescription: 'För realtidstranskription, använd vår WebSocket API för att strömma audio och få omedelbar resultat.',
      supportedLanguages: 'Stödda Språk',
      
      // Language codes
      frenchLanguage: 'Franska',
      frenchMedical: 'Franska (Medicinsk)',
      englishLanguage: 'Engelska',
      germanLanguage: 'Tyska',
      spanishLanguage: 'Spanska',
      italianLanguage: 'Italienska',
      portugueseLanguage: 'Portugisiska',
      dutchLanguage: 'Holländska',
      
      // Tab Labels
      quickStart: 'Snabbstart',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Autentisering',
      
      // Examples descriptions
      curlExamplesTitle: 'cURL Exempel',
      curlDescription: 'Använd dessa cURL kommandon för att testa API:et direkt från din terminal.',
      nodeJsTitle: 'Node.js Integration',
      nodeJsDescription: 'Komplett Node.js exempel med axios och form-data bibliotek.',
      pythonTitle: 'Python Integration',
      pythonDescription: 'Python exempel med requests biblioteket för HTTP API anrop.',
      websocketTitle: 'WebSocket Realtidstranskription',
      websocketDescription: 'För realtidstranskription, använd vår WebSocket API för att strömma audiodata och få omedelbar transkriptionsresultat.',
      
      // WebSocket details
      completeWebsocketSamples: 'Kompletta WebSocket Exempel Tillgängliga',
      checkGithubRepo: 'Kolla vår GitHub repository för kompletta WebSocket exempel i flera språk',
      viewMoreExamples: 'Visa Fler Exempel',
      websocketProtocol: 'WebSocket Protokoll',
      connectionUrl: 'Anslutnings URL',
      audioFormat: 'Audio Format',
      messageTypes: 'Meddelandetyper',
      audioFormatDetails: '16-bit PCM, 16kHz samplingsfrekvens',
      messageTypesDetails: 'Skicka binär audiodata, ta emot JSON text svar',
      
      // Authentication
      authenticationTitle: 'Autentisering',
      apiKeyAuthentication: 'API-nyckel Autentisering',
      apiKeyUsage: 'Använd din API-nyckel i X-LVL-KEY headern för HTTP förfrågningar:',
      jwtTokenAuthentication: 'JWT Token Autentisering (WebSocket)',
      jwtDescription: 'För WebSocket anslutningar behöver du en JWT token. Få en genom OAuth endpointen:',
      securityBestPractices: 'Säkerhets Bästa Praxis',
      securityTip1: 'Exponera aldrig dina API-nycklar i klientkod',
      securityTip2: 'Använd miljövariabler för att lagra credentials',
      securityTip3: 'Rotera dina API-nycklar regelbundet',
      securityTip4: 'Övervaka din API användning från utvecklardashboarden',
      
      // Footer
      needHelp: 'Behöver hjälp? Kontakta vårt supportteam eller kolla våra',
      githubSamples: 'GitHub exempel',
      forMoreExamples: 'för fler exempel.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Adminpanel',
      billingManagement: 'Faktureringshantering',
      walletCredits: 'Plånbokskrediter',
      customPricing: 'Anpassade Priser',

      // Billing Tab
      userBillingSettings: 'Användarfaktureringsinstälningar',
      billingSettingsDescription: 'Aktivera eller inaktivera automatisk månadsfakturering för användare och ställ in deras fakturadag.',
      user: 'Användare',
      email: 'E-post',
      stripeCustomer: 'Stripe Kund',
      autoInvoicing: 'Autofakturering',
      invoiceDay: 'Fakturadag',
      lastInvoice: 'Senaste Faktura',
      actions: 'Åtgärder',
      edit: 'Redigera',

      // Credits Tab
      addWalletCredits: 'Lägg till Plånbokskrediter',
      creditsDescription: 'Lägg till krediter till användarplånböcker. Detta är endast för administrativa justeringar.',
      selectUser: 'Välj Användare',
      reason: 'Anledning',
      creditReasonPlaceholder: 't.ex. Kampanjkredit, återbetalning, etc.',
      addCredit: 'Lägg till Kredit',
      adding: 'Lägger till...',

      // Pricing Tab
      customUserPricing: 'Anpassade Användarpriser',
      pricingDescription: 'Ställ in anpassade transkriptionspriser per användare per motor. Priserna är i mikroeuro per sekund.',
      engine: 'Motor',
      defaultPrice: 'Standardpris',
      currentPrice: 'Nuvarande Pris',
      newPrice: 'Nytt Pris',
      status: 'Status',
      custom: 'Anpassad',
      default: 'Standard',
      noChanges: 'Inga ändringar',
      refresh: 'Uppdatera',
      noEnginesAvailable: 'Inga motorer tillgängliga',
      selectUserToViewPricing: 'Välj en användare för att visa och redigera deras anpassade priser',

      // TTS
      'tts.title': 'Text till Tal',
      'tts.subtitle': 'Konvertera text till naturligt ljud',
      'tts.voiceLabel': 'Röst',
      'tts.loadingVoices': 'Laddar röster...',
      'tts.voicesError': 'Kunde inte ladda röster',
      'tts.textLabel': 'Text att syntetisera',
      'tts.textPlaceholder': 'Ange text här...',
      'tts.generateButton': 'Generera Tal',
      'tts.generating': 'Genererar...',
      'tts.errorGeneric': 'Kunde inte generera ljud',
      'tts.error402': 'Otillräckligt saldo. Ladda din plånbok via faktureringsmenyn för att använda TTS.',
      'tts.sampleTexts': 'Exempeltexter',
      'tts.streamingMode': 'Streamingläge',
      'tts.selectVoiceFirst': 'Välj en röst för att starta streaming',
      'tts.connecting': 'Ansluter...',
      'tts.connected': 'Ansluten',
      'tts.streaming': 'Streamar...',
      'tts.disconnected': 'Frånkopplad',
      'tts.stopStreaming': 'Stoppa',
      'tts.streamingError': 'Streamingfel',
      'tts.downloadAudio': 'Ladda ner ljud',
      'tts.play': 'Spela',
      'tts.pause': 'Paus',
      'tts.performanceMetrics': 'Prestandamått',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Total Tid',
      'tts.characters': 'Tecken',
      'tts.audioDuration': 'Ljudlängd',
      'tts.estimatedCost': 'Uppsk. Kostnad',
      'tts.requestId': 'Begäran-ID',
      voiceGallery: 'Röstgalleri',
      voiceGallerySubtitle: 'Bläddra, förhandsgranska och klona röster',
      stockVoices: 'Standardröster',
      myClonedVoices: 'Mina Klonade Röster',
      addNewVoice: 'Lägg till Ny Röst',
      cloneVoice: 'Klona Röst',
      voiceName: 'Röstnamn',
      voiceDescription: 'Beskrivning',
      uploadWavFiles: 'Ladda upp WAV-filer',
      dragDropWav: 'Dra och släpp WAV-filer här eller klicka för att bläddra',
      cloning: 'Klonar...',
      cloneSuccess: 'Röst klonad',
      deleteVoice: 'Radera Röst',
      confirmDeleteVoice: 'Är du säker på att du vill radera denna röst? Det går inte att ångra.',
      voiceDeleted: 'Röst raderad',
      noClonedVoices: 'Inga klonade röster ännu. Skapa din första anpassade röst!',
      searchVoices: 'Sök röster...',
      filterByLanguage: 'Språk',
      allLanguages: 'Alla Språk',
      allTypes: 'Alla',
      stock: 'Standard',
      cloned: 'Klonad',
      tryVoice: 'Prova',
      preview: 'Förhandsgranska',
      ttsQuickStart: 'TTS Snabbstart',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Text-till-Tal Översikt',
      ttsOverviewDescription: 'Konvertera text till naturligt ljud med vår TTS API.',
      ttsStep1: 'Hämta Din API-nyckel',
      ttsStep2: 'Lista Tillgängliga Röster',
      ttsStep3: 'Syntetisera Tal',
      ttsSupportedFormats: 'Stödda utdataformat: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'TTS REST API Referens',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Strömma text-till-tal-ljud i realtid via WebSocket-anslutningar.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Totala Tecken',
      ttsUsageNote: 'TTS-användningsspårning kommer snart. Dina TTS-kostnader visas i faktureringens transaktionshistorik.',
      ttsCost: 'TTS-kostnad',
      ttsThisMonth: 'Denna Månad',
      avgTtfb: 'Medel TTFB',
      sharedBalance: 'Delat saldo för ASR- och TTS-tjänster',
      pricing: 'Priser',
      asrPricing: 'ASR - Tal till Text',
      ttsPricing: 'TTS - Text till Tal',
      perMinuteRate: '€0,01 per minuts ljud',
      standardVoiceRate: '€0,01 per 1 000 tecken (standard)',
      clonedVoiceRate: '€0,03 per 1 000 tecken (klonad)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Spela in tal, transkribera, redigera och syntetisera om',
      'tts.recordOrUpload': 'Spela in eller ladda upp ljud',
      'tts.recordAudio': 'Spela in',
      'tts.uploadFile': 'Ladda upp fil',
      'tts.transcribing': 'Transkriberar...',
      'tts.transcriptionComplete': 'Transkription klar',
      'tts.editTranscription': 'Redigera transkription',
      'tts.proceedToTts': 'Gå till TTS',
      'tts.speakIt': 'Syntetisera',
      'tts.originalRecording': 'Originalinspelning',
      'tts.ttsOutput': 'TTS-utdata',
      'tts.step1Record': 'Spela in',
      'tts.step2Transcribe': 'Transkribera',
      'tts.step3Edit': 'Redigera',
      'tts.step4Speak': 'Syntetisera',
      'tts.startOver': 'Börja om',
      'tts.compareAudio': 'Jämför ljud',
      'tts.or': 'eller'
    }
  },
  pt: {
    translation: {
      // App title
      appTitle: 'Portal do Desenvolvedor Voxist AI',
      tagline: 'Plataforma IA de Voz - ASR & TTS',
      
      // Login form
      welcomeBack: 'Bem-vindo de volta',
      signInPrompt: 'Entre na sua conta de desenvolvedor',
      signInWithEmail: 'Entrar com Email',
      signInWithGoogle: 'Entrar com Google',
      signInWithMicrosoft: 'Entrar com Microsoft',
      
      // User menu
      userMenu: 'Menu do usuário',
      billing: 'Faturamento',
      usage: 'Uso',
      apiKeys: 'Chaves API',
      signOut: 'Sair',
      
      // Language selector
      selectLanguage: 'Selecionar Idioma',
      
      // Common
      close: 'Fechar',
      cancel: 'Cancelar',
      save: 'Salvar',
      loading: 'Carregando...',
      error: 'Erro',
      success: 'Sucesso',
      
      // API Keys
      apiKeyManagement: 'Gerenciamento de Chaves API',
      createApiKey: 'Criar Chave API',
      apiKeyName: 'Nome da Chave API',
      apiKeyDescription: 'Descrição',
      generateKey: 'Gerar Chave',
      copyKey: 'Copiar Chave',
      keyCreated: 'Chave API Criada',
      keyRotated: 'Chave API Rotacionada',
      rotateKey: 'Rotacionar Chave',
      deleteKey: 'Excluir Chave',
      confirmDelete: 'Tem certeza de que deseja excluir esta chave API?',
      keyDeleted: 'Chave API Excluída',
      showKey: 'Mostrar Chave',
      hideKey: 'Ocultar Chave',
      lastUsed: 'Último Uso',
      createdAt: 'Criada',
      neverUsed: 'Nunca Usada',
      active: 'Ativa',
      inactive: 'Inativa',
      
      // Billing & Payments  
      billingPayments: 'Faturamento e Pagamentos',
      currentBalance: 'Saldo Atual',
      addFunds: 'Adicionar Fundos',
      amount: 'Quantidade',
      topupWallet: 'Recarregar Carteira',
      paymentSuccessful: 'Pagamento Bem-sucedido',
      paymentFailed: 'Pagamento Falhou',
      minimumAmount: 'Mínimo: €0,50',
      maximumAmount: 'Máximo: €10.000,00',
      completePayment: 'Completar Pagamento',
      paymentDetails: 'Detalhes do Pagamento',
      amountToPay: 'Valor a pagar',
      testCard: 'Cartão de teste: 4242 4242 4242 4242, qualquer data futura de expiração, qualquer CVC',
      paymentComplete: 'Pagamento Concluído',
      walletUpdated: 'Sua carteira será atualizada em breve',
      
      // Transaction History
      transactionHistory: 'Histórico de Transações',
      date: 'Data',
      description: 'Descrição',
      type: 'Tipo',
      invoice: 'Fatura',
      payment: 'Pagamento',
      noTransactions: 'Nenhuma transação encontrada',
      downloadInvoice: 'Baixar Fatura',
      
      // Usage & Statistics
      usageStatistics: 'Estatísticas de Uso',
      totalCalls: 'Total de Chamadas API',
      thisMonth: 'Este Mês',
      lastMonth: 'Mês Passado',
      totalMinutes: 'Total de Minutos Processados',
      averageCallDuration: 'Duração Média da Chamada',
      mostUsedEngine: 'Motor Mais Utilizado',
      callsPerDay: 'Chamadas por Dia',
      minutesPerDay: 'Minutos por Dia',
      usageBreakdown: 'Detalhamento de Uso',
      byEngine: 'Por Motor',
      byDate: 'Por Data',
      
      // Missing keys from error logs
      usageAnalytics: 'Análise de uso',
      timeRange: 'Intervalo de tempo',
      days: 'dias',
      
      // Wallet & Credits
      walletBalance: 'Saldo da Carteira',
      availableCredits: 'Créditos Disponíveis',
      creditsUsed: 'Créditos Usados',
      creditTransaction: 'Transação de Crédito',
      welcomeBonus: 'Bônus de Boas-vindas',
      stripePayment: 'Pagamento Stripe',
      
      // Errors & Validation
      invalidAmount: 'Por favor, insira um valor válido',
      networkError: 'Erro de rede ocorreu',
      paymentIntentFailed: 'Falha na criação da intenção de pagamento',
      billingDataError: 'Falha ao carregar dados de faturamento',
      deleteKeyError: 'Falha ao excluir chave API',
      createKeyError: 'Falha ao criar chave API',
      rotateKeyError: 'Falha ao rotacionar chave API',
      
      // Developer Documentation
      developerDocs: 'Documentação para Desenvolvedores',
      developerDocsSubtitle: 'Integre a poderosa API de reconhecimento de voz da Voxist em suas aplicações',
      websocketSamples: 'Exemplos WebSocket',
      apiReference: 'Referência da API',
      
      // Features
      realtimeProcessing: 'Processamento em Tempo Real',
      realtimeDescription: 'Transmita áudio e obtenha resultados de transcrição instantâneos com nossa API WebSocket',
      multiLanguageSupport: 'Suporte Multi-idioma',
      multiLanguageDescription: 'Suporte para português, inglês, francês, alemão, espanhol, italiano, holandês e mais',
      secureReliable: 'Seguro e Confiável',
      secureReliableDescription: 'Segurança de nível empresarial com autenticação por chave API e conexões criptografadas',
      
      // Quick Start
      quickStartGuide: 'Guia de Início Rápido',
      getStartedMinutes: 'Comece em Minutos',
      followStepsIntegrate: 'Siga estes passos para integrar Voxist ASR em sua aplicação',
      getApiKey: 'Obtenha sua Chave API',
      getApiKeyDescription: 'Gere uma chave API a partir do seu painel do portal de desenvolvedores. Clique no menu do usuário e selecione "Chaves API" para criar uma nova chave.',
      uploadAudioFiles: 'Enviar Arquivos de Áudio',
      uploadAudioDescription: 'Use nossa API REST para enviar arquivos de áudio para transcrição. Formatos suportados: WAV, MP3, M4A, FLAC, OGG.',
      integrateRealtime: 'Integrar Tempo Real',
      integrateRealtimeDescription: 'Para transcrição em tempo real, use nossa API WebSocket para transmitir áudio e receber resultados instantâneos.',
      supportedLanguages: 'Idiomas Suportados',
      
      // Language codes
      frenchLanguage: 'Francês',
      frenchMedical: 'Francês (Médico)',
      englishLanguage: 'Inglês',
      germanLanguage: 'Alemão',
      spanishLanguage: 'Espanhol',
      italianLanguage: 'Italiano',
      portugueseLanguage: 'Português',
      dutchLanguage: 'Holandês',
      
      // Tab Labels
      quickStart: 'Início Rápido',
      curlExamples: 'cURL',
      nodeJsIntegration: 'Node.js',
      pythonIntegration: 'Python',
      websocketRealtime: 'WebSocket',
      authentication: 'Autenticação',
      
      // Examples descriptions
      curlExamplesTitle: 'Exemplos cURL',
      curlDescription: 'Use estes comandos cURL para testar a API diretamente do seu terminal.',
      nodeJsTitle: 'Integração Node.js',
      nodeJsDescription: 'Exemplo completo Node.js usando as bibliotecas axios e form-data.',
      pythonTitle: 'Integração Python',
      pythonDescription: 'Exemplo Python usando a biblioteca requests para chamadas HTTP da API.',
      websocketTitle: 'Transcrição WebSocket em Tempo Real',
      websocketDescription: 'Para transcrição em tempo real, use nossa API WebSocket para transmitir dados de áudio e receber resultados de transcrição instantâneos.',
      
      // WebSocket details
      completeWebsocketSamples: 'Exemplos WebSocket Completos Disponíveis',
      checkGithubRepo: 'Confira nosso repositório GitHub para exemplos WebSocket completos em múltiplas linguagens',
      viewMoreExamples: 'Ver Mais Exemplos',
      websocketProtocol: 'Protocolo WebSocket',
      connectionUrl: 'URL de Conexão',
      audioFormat: 'Formato de Áudio',
      messageTypes: 'Tipos de Mensagem',
      audioFormatDetails: 'PCM 16-bit, taxa de amostragem 16kHz',
      messageTypesDetails: 'Envie dados de áudio binários, receba respostas de texto JSON',
      
      // Authentication
      authenticationTitle: 'Autenticação',
      apiKeyAuthentication: 'Autenticação por Chave API',
      apiKeyUsage: 'Use sua chave API no cabeçalho X-LVL-KEY para requisições HTTP:',
      jwtTokenAuthentication: 'Autenticação por Token JWT (WebSocket)',
      jwtDescription: 'Para conexões WebSocket, você precisa de um token JWT. Obtenha um usando o endpoint OAuth:',
      securityBestPractices: 'Melhores Práticas de Segurança',
      securityTip1: 'Nunca exponha suas chaves API em código do lado do cliente',
      securityTip2: 'Use variáveis de ambiente para armazenar credenciais',
      securityTip3: 'Rotacione suas chaves API regularmente',
      securityTip4: 'Monitore o uso da sua API a partir do painel de desenvolvedores',
      
      // Footer
      needHelp: 'Precisa de ajuda? Entre em contato com nossa equipe de suporte ou confira nossos',
      githubSamples: 'exemplos do GitHub',
      forMoreExamples: 'para mais exemplos.',

      // Admin Panel
      admin: 'Admin',
      adminPanel: 'Painel de Administração',
      billingManagement: 'Gerenciamento de Faturamento',
      walletCredits: 'Créditos da Carteira',
      customPricing: 'Preços Personalizados',

      // Billing Tab
      userBillingSettings: 'Configurações de Faturamento de Usuários',
      billingSettingsDescription: 'Ative ou desative o faturamento automático mensal para usuários e defina seu dia de fatura.',
      user: 'Usuário',
      email: 'E-mail',
      stripeCustomer: 'Cliente Stripe',
      autoInvoicing: 'Faturamento Auto',
      invoiceDay: 'Dia da Fatura',
      lastInvoice: 'Última Fatura',
      actions: 'Ações',
      edit: 'Editar',

      // Credits Tab
      addWalletCredits: 'Adicionar Créditos à Carteira',
      creditsDescription: 'Adicione créditos às carteiras dos usuários. Isso é apenas para ajustes administrativos.',
      selectUser: 'Selecionar Usuário',
      reason: 'Motivo',
      creditReasonPlaceholder: 'ex. Crédito promocional, reembolso, etc.',
      addCredit: 'Adicionar Crédito',
      adding: 'Adicionando...',

      // Pricing Tab
      customUserPricing: 'Preços de Usuário Personalizados',
      pricingDescription: 'Defina preços de transcrição personalizados por usuário por motor. Os preços estão em microeuros por segundo.',
      engine: 'Motor',
      defaultPrice: 'Preço Padrão',
      currentPrice: 'Preço Atual',
      newPrice: 'Novo Preço',
      status: 'Status',
      custom: 'Personalizado',
      default: 'Padrão',
      noChanges: 'Sem alterações',
      refresh: 'Atualizar',
      noEnginesAvailable: 'Nenhum motor disponível',
      selectUserToViewPricing: 'Selecione um usuário para visualizar e editar seus preços personalizados',

      // TTS
      'tts.title': 'Texto para Fala',
      'tts.subtitle': 'Converta texto em fala com som natural',
      'tts.voiceLabel': 'Voz',
      'tts.loadingVoices': 'Carregando vozes...',
      'tts.voicesError': 'Falha ao carregar vozes',
      'tts.textLabel': 'Texto a sintetizar',
      'tts.textPlaceholder': 'Digite o texto aqui...',
      'tts.generateButton': 'Gerar Fala',
      'tts.generating': 'Gerando...',
      'tts.errorGeneric': 'Falha ao gerar áudio',
      'tts.error402': 'Saldo insuficiente. Por favor recarregue sua carteira pelo menu de faturamento para usar TTS.',
      'tts.sampleTexts': 'Textos de Exemplo',
      'tts.streamingMode': 'Modo streaming',
      'tts.selectVoiceFirst': 'Por favor selecione uma voz para iniciar o streaming',
      'tts.connecting': 'Conectando...',
      'tts.connected': 'Conectado',
      'tts.streaming': 'Transmitindo...',
      'tts.disconnected': 'Desconectado',
      'tts.stopStreaming': 'Parar',
      'tts.streamingError': 'Erro de streaming',
      'tts.downloadAudio': 'Baixar áudio',
      'tts.play': 'Reproduzir',
      'tts.pause': 'Pausar',
      'tts.performanceMetrics': 'Métricas de Desempenho',
      'tts.ttfb': 'TTFB',
      'tts.totalTime': 'Tempo Total',
      'tts.characters': 'Caracteres',
      'tts.audioDuration': 'Duração do Áudio',
      'tts.estimatedCost': 'Custo Est.',
      'tts.requestId': 'ID da Solicitação',
      voiceGallery: 'Galeria de Vozes',
      voiceGallerySubtitle: 'Explorar, pré-visualizar e clonar vozes',
      stockVoices: 'Vozes Padrão',
      myClonedVoices: 'Minhas Vozes Clonadas',
      addNewVoice: 'Adicionar Nova Voz',
      cloneVoice: 'Clonar Voz',
      voiceName: 'Nome da Voz',
      voiceDescription: 'Descrição',
      uploadWavFiles: 'Carregar Arquivos WAV',
      dragDropWav: 'Arraste e solte arquivos WAV aqui ou clique para explorar',
      cloning: 'Clonando...',
      cloneSuccess: 'Voz clonada com sucesso',
      deleteVoice: 'Excluir Voz',
      confirmDeleteVoice: 'Tem certeza de que deseja excluir esta voz? Esta ação não pode ser desfeita.',
      voiceDeleted: 'Voz excluída',
      noClonedVoices: 'Nenhuma voz clonada ainda. Crie sua primeira voz personalizada!',
      searchVoices: 'Pesquisar vozes...',
      filterByLanguage: 'Idioma',
      allLanguages: 'Todos os Idiomas',
      allTypes: 'Todos',
      stock: 'Padrão',
      cloned: 'Clonada',
      tryVoice: 'Experimentar',
      preview: 'Pré-visualizar',
      ttsQuickStart: 'Início Rápido TTS',
      ttsRestApi: 'TTS REST API',
      ttsWebSocket: 'TTS WebSocket',
      ttsOverview: 'Visão Geral de Texto para Fala',
      ttsOverviewDescription: 'Converta texto em fala com som natural usando nossa API TTS.',
      ttsStep1: 'Obtenha Sua Chave API',
      ttsStep2: 'Liste as Vozes Disponíveis',
      ttsStep3: 'Sintetize a Fala',
      ttsSupportedFormats: 'Formatos de saída suportados: wav, pcm_f32le, pcm_s16le',
      ttsRestTitle: 'Referência TTS REST API',
      ttsWsTitle: 'TTS WebSocket Streaming',
      ttsWsDescription: 'Transmita áudio de texto para fala em tempo real usando conexões WebSocket.',
      asrUsage: 'ASR',
      ttsUsage: 'TTS',
      totalCharacters: 'Total de Caracteres',
      ttsUsageNote: 'O rastreamento de uso de TTS estará disponível em breve. Seus custos de TTS aparecem no histórico de transações de faturamento.',
      ttsCost: 'Custo TTS',
      ttsThisMonth: 'Este Mês',
      avgTtfb: 'TTFB Méd.',
      sharedBalance: 'Saldo compartilhado para serviços ASR e TTS',
      pricing: 'Preços',
      asrPricing: 'ASR - Voz para Texto',
      ttsPricing: 'TTS - Texto para Voz',
      perMinuteRate: '€0,01 por minuto de áudio',
      standardVoiceRate: '€0,01 por 1.000 caracteres (padrão)',
      clonedVoiceRate: '€0,03 por 1.000 caracteres (clonada)',
      'tts.roundTrip': 'Round-Trip',
      'tts.roundTripDescription': 'Gravar fala, transcrever, editar e re-sintetizar',
      'tts.recordOrUpload': 'Gravar ou carregar áudio',
      'tts.recordAudio': 'Gravar',
      'tts.uploadFile': 'Carregar arquivo',
      'tts.transcribing': 'Transcrevendo...',
      'tts.transcriptionComplete': 'Transcrição concluída',
      'tts.editTranscription': 'Editar transcrição',
      'tts.proceedToTts': 'Ir para TTS',
      'tts.speakIt': 'Sintetizar',
      'tts.originalRecording': 'Gravação original',
      'tts.ttsOutput': 'Saída TTS',
      'tts.step1Record': 'Gravar',
      'tts.step2Transcribe': 'Transcrever',
      'tts.step3Edit': 'Editar',
      'tts.step4Speak': 'Sintetizar',
      'tts.startOver': 'Recomeçar',
      'tts.compareAudio': 'Comparar áudio',
      'tts.or': 'ou'
    }
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    lng: getDefaultUILanguage(), // Set initial language from browser locale
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng'
    },

    interpolation: {
      escapeValue: false
    },
    
    react: {
      useSuspense: false
    }
  })

export default i18n